import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { toLocalDateStr } from '../lib/utils';
import { subDays, previousFriday } from 'date-fns';
import { JiraIssueDetail } from './JiraIssueDetail';
import { CombinedIndicators } from './CombinedIndicators';
import { PresetGrid } from './PresetGrid';
import * as api from '@/services/api';
import * as presetStore from '@/services/preset-store';
import { getStatusColor } from '@/services/jira-status-config';
import { withJiraExpiryCheck, type JiraCredentials } from '@/services/jira-token-expiry';
import { useJiraTokenExpiryModal } from '@/components/JiraTokenExpiryModal';
import { WaterfallView, type WaterfallEvent } from './WaterfallView';
import { createConfluenceService } from '@/services/confluence-service';
import { createNotionService } from '@/services/notion-service';
import { createGoogleDocsService } from '@/services/google-docs-service';
import { ConfluenceSpaceCombobox } from './ConfluenceSpaceCombobox';
import { ConfluencePageDetail } from './ConfluencePageDetail';
import { CommentEventDetail } from './ConfluenceActivityView';
import { generateAndDownloadReport } from '@/services/api';
import { getStoredGitAliases, resolveGitAuthor } from './SettingsView';
import type { Preset, AnalysisJob, HydratedSnapshot, SnapshotData } from '@/services/preset-types';
import { resolveTimeRange, ENGINE_VERSION } from '@/services/preset-types';
import { notifyAnalysisComplete } from '@/services/notifications';
import { NotificationPermissionDialog } from './NotificationPermissionDialog';
import * as historyStore from '@/services/analysis-history-store';
import * as audit from '@/services/audit';
import { usePresetShortcuts } from '@/hooks/usePresetShortcuts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faArrowTrendUp,
  faArrowRightArrowLeft,
  faBolt,
  faCalendarDays,
  faChartBar,
  faChartPie,
  faChevronDown,
  faChevronRight,
  faCircleCheck,
  faCircleExclamation,
  faCirclePlus,
  faCodeBranch,
  faCodeCommit,
  faComment,
  faDownload,
  faEye,
  faEyeSlash,
  faFile,
  faFilter,
  faFlag,
  faLayerGroup,
  faMagnifyingGlass,
  faMoon,
  faPenToSquare,
  faPencil,
  faPlus,
  faRotate,
  faSpinner,
  faSquare,
  faSquareCheck,
  faSun,
  faThumbtack,
  faUserPen,
  faUsers,
  faXmark
} from '@fortawesome/free-solid-svg-icons';
import { faNotion, faGoogle } from '@fortawesome/free-brands-svg-icons';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell, Legend
} from 'recharts';

const mkIcon = (def: import('@fortawesome/fontawesome-svg-core').IconDefinition) =>
  ({ className = '' }: { className?: string }) =>
    <FontAwesomeIcon icon={def} className={className} />;

// ==================== Types ====================

interface AvailableRepo {
  id: string;
  name: string;
  type: 'local' | 'bitbucket' | 'github' | 'gitlab';
  url?: string;
  path?: string;
  icon?: string;
  description?: string;
  project?: string;
  updatedOn?: string;
}

interface RepoConfig {
  type: string;
  path?: string;
  url?: string;
  accessToken?: string;
  username?: string;
  appPassword?: string;
  name?: string;
  repoId?: string;
}

interface GitCommitItem {
  hash: string;
  author: string;
  email: string;
  date: string;
  message: string;
  branch: string;
  additions: number;
  deletions: number;
  isMerge?: boolean;
  repoName: string;
  repoId: string;
}

interface JiraEvent {
  id: string;
  timestamp: string;
  type: string;
  issueKey: string;
  issueSummary: string;
  issueType: string;
  author: { displayName: string; accountId: string; avatarUrl?: string };
  fromStatus?: string;
  toStatus?: string;
  fromAssignee?: string;
  toAssignee?: string;
  fromSprint?: string;
  toSprint?: string;
  fromValue?: string;
  toValue?: string;
  labels?: string[];
  commentBody?: string;
}

interface JiraProject {
  id: string;
  key: string;
  name: string;
}

interface ConfluenceSpace {
  key: string;
  name: string;
  type?: 'global' | 'personal' | 'knowledge_base' | string;
}

interface ConfluencePageEvent {
  id: string;
  pageId: string;
  pageTitle: string;
  spaceKey: string;
  spaceName: string;
  authorId: string;
  authorName: string;
  timestamp: string;
  version: number;
  isNew: boolean;
  url: string;
  eventType?: string;
  commentId?: string;
  commentVersion?: number;
  commentBody?: string;
  commentType?: 'footer' | 'inline';
  minorEdit?: boolean;
  versionMessage?: string;
}

interface NotionPageEvent {
  id: string;
  title: string;
  url: string;
  createdTime: string;
  lastEditedTime: string;
  createdBy: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
  lastEditedBy: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
  parent: any;
  parentType?: string;
  archived: boolean;
}

interface GoogleDocEvent {
  id: string;
  title: string;
  url: string;
  mimeType: string;
  createdTime: string;
  modifiedTime: string;
  lastModifyingUser?: {
    emailAddress: string;
    displayName: string;
    photoLink?: string;
  };
  parents: string[];
  starred: boolean;
  trashed: boolean;
  eventType?: 'created' | 'updated' | 'commented';
  authorName?: string;
  authorEmail?: string;
  timestamp?: string;
}

// Unified timeline item — can be a git commit, jira event, confluence page edit, notion event, or google doc event
interface TimelineItem {
  id: string;
  timestamp: string;
  source: 'git' | 'jira' | 'confluence' | 'notion' | 'google-docs';
  // Normalized user identity
  userName: string;
  userEmail?: string;
  // Git commit fields
  gitCommit?: GitCommitItem;
  // Jira event fields
  jiraEvent?: JiraEvent;
  // Confluence page event fields
  confluenceEvent?: ConfluencePageEvent;
  // Notion page event fields
  notionEvent?: NotionPageEvent;
  // Google Docs event fields
  googleDocEvent?: GoogleDocEvent;
}

interface UserProfile {
  name: string;
  emails: Set<string>;
  jiraAccountId?: string;
  jiraAvatarUrl?: string;
  gitCommits: number;
  jiraEvents: number;
  additions: number;
  deletions: number;
}

export interface CombinedReviewData {
  gitCommits: GitCommitItem[];
  jiraEvents: JiraEvent[];
  jiraSprintSummary: any[];
  confluenceEvents?: ConfluencePageEvent[];
  notionEvents?: NotionPageEvent[];
  googleDocEvents?: GoogleDocEvent[];
  selectedRepoNames: string[];
  selectedJiraProject: string;
  selectedConfluenceSpace?: string;
  customFrom: string;
  customTo: string;
}

interface CombinedProjectViewProps {
  availableRepos: AvailableRepo[];
  onClose: () => void;
  storedUsername?: string | null;
  storedEmail?: string | null;
  storedToken?: string | null;
  jiraDomain?: string;
  jiraEmail?: string;
  jiraApiToken?: string;
  defaultProjectKey?: string;
  defaultBoardId?: number;
  inline?: boolean;
  reviewData?: CombinedReviewData;
  confluenceEnabled?: boolean;
  confluenceDomain?: string;
  notionEnabled?: boolean;
  googleDocsEnabled?: boolean;
}

// ==================== Helpers ====================

function normalizeNameForMatch(name: string): string {
  return name.toLowerCase().trim().replace(/[^a-z0-9 ]/g, '');
}

// getStatusColor imported from @/services/jira-status-config

function formatTime(ts: string): string {
  return new Date(ts).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
}

// Extract Jira issue keys (e.g. PROJ-1234, DED-2304) from a string
const JIRA_KEY_REGEX = /\b([A-Z][A-Z0-9_]+-\d+)\b/g;
function extractJiraKeys(text: string): string[] {
  const matches = text.toUpperCase().match(JIRA_KEY_REGEX);
  return matches ? [...new Set(matches)] : [];
}

interface LinkedGroup {
  issueKey: string;
  issueSummary?: string;
  issueType?: string;
  latestStatus?: string;
  commits: GitCommitItem[];
  jiraEvents: JiraEvent[];
}

function formatDateLabel(dateStr: string): string {
  const d = new Date(dateStr);
  const today = toLocalDateStr();
  const yesterday = toLocalDateStr(new Date(Date.now() - 86400000));
  if (dateStr === today) return 'Today';
  if (dateStr === yesterday) return 'Yesterday';
  return d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
}

const PROJECT_COLORS = [
  { bg: 'bg-blue-500', text: 'text-blue-400', border: 'border-blue-500/30', light: 'bg-blue-500/10', dot: 'bg-blue-500' },
  { bg: 'bg-emerald-500', text: 'text-emerald-400', border: 'border-emerald-500/30', light: 'bg-emerald-500/10', dot: 'bg-emerald-500' },
  { bg: 'bg-amber-500', text: 'text-amber-400', border: 'border-amber-500/30', light: 'bg-amber-500/10', dot: 'bg-amber-500' },
  { bg: 'bg-purple-500', text: 'text-purple-400', border: 'border-purple-500/30', light: 'bg-purple-500/10', dot: 'bg-purple-500' },
  { bg: 'bg-rose-500', text: 'text-rose-400', border: 'border-rose-500/30', light: 'bg-rose-500/10', dot: 'bg-rose-500' },
  { bg: 'bg-cyan-500', text: 'text-cyan-400', border: 'border-cyan-500/30', light: 'bg-cyan-500/10', dot: 'bg-cyan-500' },
];

// ==================== Component ====================

export function CombinedProjectView({
  availableRepos, onClose, storedUsername, storedEmail: _storedEmail, storedToken,
  jiraDomain, jiraEmail, jiraApiToken, defaultProjectKey, defaultBoardId, inline, reviewData, confluenceEnabled,
  notionEnabled, googleDocsEnabled,
}: CombinedProjectViewProps) {
  const hasConfluence = !!confluenceEnabled;
  const hasNotion = !!notionEnabled;
  const hasGoogleDocs = !!googleDocsEnabled;
  const hasJira = !!(jiraDomain && jiraEmail && jiraApiToken);

  // Token expiry modal
  const { Modal: TokenExpiryModal } = useJiraTokenExpiryModal();

  // Git state
  const [selectedRepoIds, setSelectedRepoIds] = useState<Set<string>>(new Set());
  const [gitCommits, setGitCommits] = useState<GitCommitItem[]>([]);
  const [gitLoading, setGitLoading] = useState(false);
  const [gitProgressMessage, setGitProgressMessage] = useState('');
  const [gitProgressIndex, setGitProgressIndex] = useState(0);
  const [gitProgressTotal, setGitProgressTotal] = useState(0);

  // Jira state
  const [jiraProjects, setJiraProjects] = useState<JiraProject[]>([]);
  const [selectedJiraProject, setSelectedJiraProject] = useState(defaultProjectKey || '');
  const [jiraEvents, setJiraEvents] = useState<JiraEvent[]>([]);
  const [jiraSprintSummary, setJiraSprintSummary] = useState<any[]>([]);
  const [jiraLoading, setJiraLoading] = useState(false);
  const [jiraProjectsLoading, setJiraProjectsLoading] = useState(false);

  // Confluence state
  const [confluenceSpaces, setConfluenceSpaces] = useState<ConfluenceSpace[]>([]);
  const [selectedConfluenceSpace, setSelectedConfluenceSpace] = useState('');
  const [confluenceEvents, setConfluenceEvents] = useState<ConfluencePageEvent[]>([]);
  const [confluenceLoading, setConfluenceLoading] = useState(false);
  const [confluenceSpacesLoading, setConfluenceSpacesLoading] = useState(false);

  // Notion state
  const [notionEvents, setNotionEvents] = useState<NotionPageEvent[]>([]);
  const [notionLoading, setNotionLoading] = useState(false);

  // Google Docs state
  const [googleDocEvents, setGoogleDocEvents] = useState<GoogleDocEvent[]>([]);
  const [googleDocsLoading, setGoogleDocsLoading] = useState(false);

  // Shared state
  const [dateRangePreset, setDateRangePreset] = useState<number | 'friday-monday'>(30);
  const [customFrom, setCustomFrom] = useState<string>(() => {
    const d = new Date(); d.setDate(d.getDate() - 30); return toLocalDateStr(d);
  });
  const [customTo, setCustomTo] = useState<string>(toLocalDateStr());
  const [showDatePicker, setShowDatePicker] = useState(false);
  const lookbackDays = Math.max(1, Math.ceil((new Date(customTo).getTime() - new Date(customFrom).getTime()) / 86400000));
  const [error, setError] = useState<string | null>(null);
  const [showSelector, setShowSelector] = useState(true);

  // Data readiness — UI only shows when both sources have finished loading
  const [dataReady, setDataReady] = useState(false);
  const [isReview, setIsReview] = useState(false);

  // Filters
  const [filterUser, setFilterUser] = useState('');
  const [filterSource, setFilterSource] = useState<'all' | 'git' | 'jira' | 'confluence' | 'notion' | 'google-docs'>('all');
  const [filterSearch, setFilterSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLabel, setFilterLabel] = useState('');
  const [showAllLabels, setShowAllLabels] = useState(false);
  const [filterIssueType, setFilterIssueType] = useState('');
  const [filterSprint, setFilterSprint] = useState('');
  const [filterRepo, setFilterRepo] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [showLinkedOnly, setShowLinkedOnly] = useState(false);
  const [showUnlinkedOnly, setShowUnlinkedOnly] = useState(false);
  const [activeTab, setActiveTab] = useState<'timeline' | 'indicators' | 'charts'>('timeline');
  const [showGitCommits, setShowGitCommits] = useState(true);
  const [showJiraStatus, setShowJiraStatus] = useState(true);
  const [showJiraAssignments, setShowJiraAssignments] = useState(true);
  const [showJiraSprints, setShowJiraSprints] = useState(true);
  const [showJiraCreated, setShowJiraCreated] = useState(true);
  const [showJiraComments, setShowJiraComments] = useState(true);
  const [hideMerges, setHideMerges] = useState(false);
  const [filterConfluenceEventTypes, setFilterConfluenceEventTypes] = useState<Set<string>>(new Set());
  const [filterConfluenceSpace, setFilterConfluenceSpace] = useState('');
  const [groupByTicket, setGroupByTicket] = useState(false);
  const [expandedTickets, setExpandedTickets] = useState<Set<string>>(new Set());
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));
  const [repoSearch, setRepoSearch] = useState('');

  // Preset state
  const [activePreset, setActivePreset] = useState<Preset | null>(null);
  const [allPresets, setAllPresets] = useState<Preset[]>([]);
  const [analysisJobs, setAnalysisJobs] = useState<Map<string, AnalysisJob>>(new Map());
  const analysisQueueRef = useRef<Set<string>>(new Set());

  // Load presets list for tab bar
  const refreshPresets = useCallback(async () => {
    const presets = await presetStore.getAllPresets();
    setAllPresets(presets);
  }, []);
  useEffect(() => { refreshPresets(); }, [refreshPresets]);

  // Sync isDark with app's dark mode class
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Accordion state for inline detail/diff
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [diff, setDiff] = useState<string>('');
  const [diffLoading, setDiffLoading] = useState(false);
  const [expandedDiffFiles, setExpandedDiffFiles] = useState<Set<number>>(new Set());

  // Hydrate from reviewData (past analysis reopen)
  useEffect(() => {
    if (!reviewData) return;
    setGitCommits(reviewData.gitCommits);
    setJiraEvents(reviewData.jiraEvents);
    setJiraSprintSummary(reviewData.jiraSprintSummary);
    setConfluenceEvents(reviewData.confluenceEvents || []);
    setNotionEvents(reviewData.notionEvents || []);
    setGoogleDocEvents(reviewData.googleDocEvents || []);
    setSelectedJiraProject(reviewData.selectedJiraProject);
    setSelectedConfluenceSpace(reviewData.selectedConfluenceSpace || '');
    setCustomFrom(reviewData.customFrom);
    setCustomTo(reviewData.customTo);
    setDataReady(true);
    setShowSelector(false);
    setIsReview(true);
    const today = toLocalDateStr();
    const yesterday = toLocalDateStr(new Date(Date.now() - 86400000));
    setExpandedDates(new Set([today, yesterday]));
  }, [reviewData]);

  // Load Confluence spaces on mount
  useEffect(() => {
    if (!hasConfluence || reviewData) return;
    setConfluenceSpacesLoading(true);
    createConfluenceService().then(svc => {
      if (!svc) return;
      return svc.getSpaces();
    }).then(result => {
      if (result) setConfluenceSpaces(result.spaces.map(s => ({ key: s.key, name: s.name, type: s.type })));
    }).catch(err => console.error('Failed to load Confluence spaces:', err))
      .finally(() => setConfluenceSpacesLoading(false));
  }, [hasConfluence, reviewData]);

  // Load Notion events on mount
  useEffect(() => {
    if (!notionEnabled || reviewData) return;
    setNotionLoading(true);
    createNotionService().then(svc => {
      if (!svc) return;
      return svc.getEvents(new Date(customFrom), new Date(customTo));
    }).then(result => {
      if (result) setNotionEvents(result.events);
    }).catch(err => console.error('Failed to load Notion events:', err))
      .finally(() => setNotionLoading(false));
  }, [notionEnabled, reviewData, customFrom, customTo]);

  // Load Google Docs events on mount
  useEffect(() => {
    if (!googleDocsEnabled || reviewData) return;
    setGoogleDocsLoading(true);
    createGoogleDocsService().then(svc => {
      if (!svc) return;
      return svc.getEvents(new Date(customFrom), new Date(customTo));
    }).then(result => {
      if (result) setGoogleDocEvents(result.events);
    }).catch(err => console.error('Failed to load Google Docs events:', err))
      .finally(() => setGoogleDocsLoading(false));
  }, [googleDocsEnabled, reviewData, customFrom, customTo]);

  // Load Jira projects on mount
  useEffect(() => {
    if (!hasJira || reviewData) return;
    setJiraProjectsLoading(true);
    const load = async () => {
      try {
        const settings: JiraCredentials = { domain: jiraDomain!, email: jiraEmail!, apiToken: jiraApiToken! };
        const result = await withJiraExpiryCheck(settings, () =>
          api.getJiraProjects(jiraDomain!, jiraEmail!, jiraApiToken!)
        );
        
        if (result.error) {
          console.error('Jira authentication error:', result.error);
          return;
        }
        
        const data = result.result;
        if (data) {
          if (Array.isArray(data)) setJiraProjects(data);
          else if (data && typeof data === 'object' && 'projects' in data) {
            setJiraProjects((data as any).projects);
          }
        }
      } catch (err) {
        console.error('Failed to load Jira projects:', err);
      } finally {
        setJiraProjectsLoading(false);
      }
    };
    load();
  }, [hasJira, jiraDomain, jiraEmail, jiraApiToken]);

  // Lock body scroll while this full-screen dialog is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  // ESC to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const toggleRepo = useCallback((id: string) => {
    setSelectedRepoIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const filteredRepos = useMemo(() => {
    const sorted = [...availableRepos].sort((a, b) => {
      const dateA = a.updatedOn ? new Date(a.updatedOn).getTime() : 0;
      const dateB = b.updatedOn ? new Date(b.updatedOn).getTime() : 0;
      return dateB - dateA;
    });
    if (!repoSearch) return sorted;
    const q = repoSearch.toLowerCase();
    return sorted.filter(r => r.name.toLowerCase().includes(q) || (r.description || '').toLowerCase().includes(q));
  }, [availableRepos, repoSearch]);

  // ==================== FETCH BOTH (parallel, wait for all) ====================
  const fetchAll = useCallback(async () => {
    setError(null);
    setDataReady(false);

    const fetchStartTime = Date.now();
    const fromDate = new Date(customFrom);
    const toDate = new Date(customTo);
    const weeks = Math.max(1, Math.ceil((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24 * 7)));
    audit.auditAnalysisStarted('combined', { repos: Array.from(selectedRepoIds).map(id => availableRepos.find(r => r.id === id)?.name || id), jiraProject: selectedJiraProject, from: customFrom, to: customTo });

    let fetchedCommitCount = 0;
    let fetchedEventCount = 0;
    let fetchedGitCommits: GitCommitItem[] = [];
    let fetchedJiraEvents: JiraEvent[] = [];
    let fetchedSprintSummary: any[] = [];
    const promises: Promise<void>[] = [];

    // Git fetch
    if (selectedRepoIds.size > 0) {
      setGitLoading(true);
      setGitProgressMessage('Preparing...');
      setGitProgressIndex(0);
      promises.push(
        (async () => {
          try {
            const configs: RepoConfig[] = availableRepos
              .filter(r => selectedRepoIds.has(r.id))
              .map(r => {
                const config: RepoConfig = { type: r.type, url: r.url, path: r.path, name: r.name, repoId: r.id };
                if (storedToken && (r.type === 'bitbucket' || r.type === 'github')) {
                  config.username = storedUsername || '';
                  config.appPassword = storedToken;
                }
                return config;
              });
            setGitProgressTotal(configs.length);
            const multiResult = await api.analyzeMultiRepo(
              configs.map(c => ({ ...c, weeks, startDate: fromDate.toISOString(), endDate: toDate.toISOString() })) as any,
              `combined-${Date.now()}`,
              (msg: string) => {
                setGitProgressMessage(msg);
                const m = msg.match(/^\[(\d+)\/(\d+)\]/);
                if (m) setGitProgressIndex(parseInt(m[1]));
              },
            );
            const allCommits = multiResult.results.flatMap(r => 
              r.commits.map(c => ({ ...c, repoName: r.repoName, repoId: r.repoId }))
            );
            setGitCommits(allCommits as any);
            fetchedGitCommits = allCommits as any;
            fetchedCommitCount = allCommits.length;
          } catch (e: any) {
            setError(prev => prev ? `${prev}\n${e.message}` : (e.message || 'Failed to fetch git data'));
          } finally {
            setGitLoading(false);
          }
        })()
      );
    }

    // Jira fetch
    if (hasJira && selectedJiraProject) {
      setJiraLoading(true);
      promises.push(
        (async () => {
          try {
            const settings: JiraCredentials = { domain: jiraDomain!, email: jiraEmail!, apiToken: jiraApiToken! };
            const result = await withJiraExpiryCheck(settings, () =>
              api.getJiraActivity(
                jiraDomain!, jiraEmail!, jiraApiToken!, selectedJiraProject, lookbackDays, defaultBoardId, customFrom, customTo
              )
            );
            
            if (result.error && result.error.message) {
                setError(prev => prev ? `${prev}\n${result.error!.message}` : result.error!.message);
              } else {
              const data = result.result;
              if (data) {
                const events = data.events || [];
                setJiraEvents(events);
                fetchedJiraEvents = events;
                setJiraSprintSummary(data.sprintSummary || []);
                fetchedSprintSummary = data.sprintSummary || [];
                fetchedEventCount = events.length;
              }
            }
          } catch (e: any) {
            setError(prev => prev ? `${prev}\n${e.message}` : (e.message || 'Failed to fetch Jira data'));
          } finally {
            setJiraLoading(false);
          }
        })()
      );
    }

    // Confluence fetch
    let fetchedConfluenceEvents: ConfluencePageEvent[] = [];
    if (hasConfluence && selectedConfluenceSpace) {
      setConfluenceLoading(true);
      promises.push(
        (async () => {
          try {
            const svc = await createConfluenceService();
            if (svc) {
              const pages = await svc.getPagesForSpaceOrAll(selectedConfluenceSpace, customFrom, customTo);
              const versionEvents = await svc.expandPagesToVersionEvents(pages, customFrom, customTo);
              const ids = [...new Set(versionEvents.map((p: any) => p.authorId).filter(Boolean))];
              const nameMap = await svc.resolveUserNames(ids);
              fetchedConfluenceEvents = versionEvents.map((p: any) => ({
                id: p.commentId ? `comment-${p.commentId}-v${p.commentVersion ?? p.version ?? 1}` : `${p.id}-v${p.version}`,
                pageId: p.id,
                pageTitle: p.title,
                spaceKey: p.spaceKey,
                spaceName: p.spaceName ?? '',
                authorId: p.authorId,
                authorName: nameMap.get(p.authorId) ?? p.authorId ?? '',
                timestamp: p.updatedAt,
                version: p.version,
                isNew: p.version === 1,
                url: p.url,
                eventType: p.eventType ?? (p.version === 1 ? 'page_created' : 'page_edited'),
                commentId: p.commentId,
                commentVersion: p.commentVersion,
                commentBody: p.commentBody,
                commentType: p.commentType,
              }));
              setConfluenceEvents(fetchedConfluenceEvents);
            }
          } catch (e: any) {
            console.error('Failed to fetch Confluence data:', e);
          } finally {
            setConfluenceLoading(false);
          }
        })()
      );
    }

    // Notion fetch
    let fetchedNotionEvents: NotionPageEvent[] = [];
    if (hasNotion) {
      setNotionLoading(true);
      promises.push(
        (async () => {
          try {
            const svc = await createNotionService();
            if (svc) {
              const result = await svc.getEvents(new Date(customFrom), new Date(customTo));
              fetchedNotionEvents = result.events || [];
              setNotionEvents(fetchedNotionEvents);
            }
          } catch (e: any) {
            console.error('Failed to fetch Notion data:', e);
          } finally {
            setNotionLoading(false);
          }
        })()
      );
    }

    // Google Docs fetch
    let fetchedGoogleDocEvents: GoogleDocEvent[] = [];
    if (hasGoogleDocs) {
      setGoogleDocsLoading(true);
      promises.push(
        (async () => {
          try {
            const svc = await createGoogleDocsService();
            if (svc) {
              const result = await svc.getEvents(new Date(customFrom), new Date(customTo));
              fetchedGoogleDocEvents = result.events || [];
              setGoogleDocEvents(fetchedGoogleDocEvents);
            }
          } catch (e: any) {
            console.error('Failed to fetch Google Docs data:', e);
          } finally {
            setGoogleDocsLoading(false);
          }
        })()
      );
    }

    // Wait for ALL fetches to complete before revealing UI
    await Promise.all(promises);

    const durationMs = Date.now() - fetchStartTime;
    audit.auditAnalysisCompleted('combined', durationMs, { commitCount: fetchedCommitCount, eventCount: fetchedEventCount });
    notifyAnalysisComplete({
      mode: 'combined',
      commitCount: fetchedCommitCount,
      eventCount: fetchedEventCount,
      durationMs,
      success: true,
    });

    // Save to analysis history
    const repoNames = Array.from(selectedRepoIds).map(id => availableRepos.find(r => r.id === id)?.name || id);
    const snapId = historyStore.generateSnapshotId();
    historyStore.saveSnapshot(
      {
        id: snapId,
        mode: 'combined',
        title: `Combined — ${repoNames.join(', ')}${selectedJiraProject ? ` + ${selectedJiraProject}` : ''}`,
        timestamp: new Date().toISOString(),
        durationMs,
        success: true,
        summary: {
          commitCount: fetchedCommitCount,
          eventCount: fetchedEventCount,
          authorCount: 0,
          repoCount: selectedRepoIds.size,
          dateRange: { start: customFrom, end: customTo },
        },
      },
      {
        gitCommits: fetchedGitCommits,
        jiraEvents: fetchedJiraEvents,
        jiraSprintSummary: fetchedSprintSummary,
        confluenceEvents: fetchedConfluenceEvents,
        notionEvents: fetchedNotionEvents,
        googleDocEvents: fetchedGoogleDocEvents,
        selectedRepoNames: repoNames,
        selectedJiraProject,
        selectedConfluenceSpace,
        customFrom,
        customTo,
      } satisfies CombinedReviewData
    ).catch(err => console.error('[analysis-history] Failed to save combined snapshot:', err));

    setDataReady(true);
    setShowSelector(false);
    const today = toLocalDateStr();
    const yesterday = toLocalDateStr(new Date(Date.now() - 86400000));
    setExpandedDates(new Set([today, yesterday]));
  }, [selectedRepoIds, availableRepos, storedUsername, storedToken, hasJira, selectedJiraProject, hasConfluence, selectedConfluenceSpace, hasNotion, hasGoogleDocs, customFrom, customTo, jiraDomain, jiraEmail, jiraApiToken, defaultBoardId]);

  // ==================== PRESET HANDLERS ====================

  const handleAnalyzePreset = useCallback(async (preset: Preset) => {
    // Don't allow duplicate concurrent analyses for same preset
    if (analysisQueueRef.current.has(preset.id)) return;
    analysisQueueRef.current.add(preset.id);

    // Set job status
    setAnalysisJobs(prev => {
      const next = new Map(prev);
      next.set(preset.id, { presetId: preset.id, status: 'running', progress: 0, message: 'Starting...' });
      return next;
    });

    const startTime = Date.now();
    const { from: fromDate, to: toDate } = resolveTimeRange(preset.timeRange);

    try {
      const weeks = Math.max(1, Math.ceil((toDate.getTime() - fromDate.getTime()) / (1000 * 60 * 60 * 24 * 7)));
      let snapshotGitCommits: any[] = [];
      let snapshotJiraEvents: any[] = [];
      let snapshotJiraSprintSummary: any[] = [];

      const promises: Promise<void>[] = [];

      // Git
      if (preset.repoIds.length > 0) {
        promises.push((async () => {
          const configs = availableRepos
            .filter(r => preset.repoIds.includes(r.id))
            .map(r => {
              const config: any = { type: r.type, url: r.url, path: r.path, name: r.name, repoId: r.id };
              if (storedToken && (r.type === 'bitbucket' || r.type === 'github')) {
                config.username = storedUsername || '';
                config.appPassword = storedToken;
              }
              return config;
            });

          const multiResult = await api.analyzeMultiRepo(
            configs.map(c => ({ ...c, weeks, startDate: fromDate.toISOString(), endDate: toDate.toISOString() })),
            `preset-${preset.id}-${Date.now()}`,
            (msg: string) => {
              const m = msg.match(/^\[(\d+)\/(\d+)\]/);
              const progress = m ? Math.round((parseInt(m[1]) / parseInt(m[2])) * 50) : 0;
              setAnalysisJobs(prev => {
                const next = new Map(prev);
                next.set(preset.id, { presetId: preset.id, status: 'running', progress, message: msg });
                return next;
              });
            },
          );
          snapshotGitCommits = multiResult.results.flatMap((r: any) =>
            r.commits.map((c: any) => ({ ...c, repoName: r.repoName, repoId: r.repoId }))
          );
        })());
      }

      // Confluence
      let snapshotConfluenceEvents: ConfluencePageEvent[] = [];
      if (hasConfluence && preset.confluenceSpaceKey) {
        promises.push((async () => {
          try {
            const svc = await createConfluenceService();
            if (svc) {
              const pages = await svc.getPagesForSpaceOrAll(
                preset.confluenceSpaceKey!, toLocalDateStr(fromDate), toLocalDateStr(toDate)
              );
              const versionEvents = await svc.expandPagesToVersionEvents(pages, toLocalDateStr(fromDate), toLocalDateStr(toDate));
              const ids = [...new Set(versionEvents.map((p: any) => p.authorId).filter(Boolean))];
              const nameMap = await svc.resolveUserNames(ids);
              snapshotConfluenceEvents = versionEvents.map((p: any) => ({
                id: p.commentId ? `comment-${p.commentId}-v${p.commentVersion ?? p.version ?? 1}` : `${p.id}-v${p.version}`,
                pageId: p.id,
                pageTitle: p.title,
                spaceKey: p.spaceKey,
                spaceName: p.spaceName ?? '',
                authorId: p.authorId,
                authorName: nameMap.get(p.authorId) ?? p.authorId ?? '',
                timestamp: p.updatedAt,
                version: p.version,
                isNew: p.version === 1,
                url: p.url,
                eventType: p.eventType ?? (p.version === 1 ? 'page_created' : 'page_edited'),
                commentId: p.commentId,
                commentVersion: p.commentVersion,
                commentBody: p.commentBody,
                commentType: p.commentType,
              }));
            }
          } catch (e: any) {
            console.error('Failed to fetch Confluence data for preset:', e);
          }
        })());
      }

      // Jira
      if (hasJira && preset.jiraProjectKey && jiraDomain && jiraEmail && jiraApiToken) {
        promises.push((async () => {
          setAnalysisJobs(prev => {
            const next = new Map(prev);
            const existing = next.get(preset.id);
            next.set(preset.id, { ...existing!, message: 'Loading Jira data...', progress: existing?.progress || 50 });
            return next;
          });
          const lookback = Math.max(1, Math.ceil((toDate.getTime() - fromDate.getTime()) / 86400000));
          const boardId = preset.jiraBoardId ?? defaultBoardId;
          // Convert to YYYY-MM-DD strings — Jira JQL doesn't accept full ISO timestamps
          const jiraFrom = toLocalDateStr(fromDate);
          const jiraTo = toLocalDateStr(toDate);
          
          const settings: JiraCredentials = { domain: jiraDomain!, email: jiraEmail!, apiToken: jiraApiToken! };
          const result = await withJiraExpiryCheck(settings, () =>
            api.getJiraActivity(
              jiraDomain!, jiraEmail!, jiraApiToken!,
              preset.jiraProjectKey!, lookback, boardId,
              jiraFrom, jiraTo
            )
          );
          
          if (!result.error && result.result) {
            snapshotJiraEvents = result.result.events || [];
            snapshotJiraSprintSummary = result.result.sprintSummary || [];
          }
        })());
      }

      await Promise.all(promises);

      // Save snapshot
      const snapshotData: SnapshotData = {
        gitCommits: snapshotGitCommits,
        jiraEvents: snapshotJiraEvents,
        jiraSprintSummary: snapshotJiraSprintSummary,
        confluenceEvents: snapshotConfluenceEvents,
        selectedJiraProject: preset.jiraProjectKey || '',
        selectedConfluenceSpace: preset.confluenceSpaceKey || '',
        selectedRepoNames: preset.repoIds.map(id => availableRepos.find(r => r.id === id)?.name || id),
        customFrom: toLocalDateStr(fromDate),
        customTo: toLocalDateStr(toDate),
      };

      const meta = {
        id: crypto.randomUUID(),
        presetId: preset.id,
        resolvedFrom: fromDate.toISOString(),
        resolvedTo: toDate.toISOString(),
        analysedAt: new Date().toISOString(),
        durationMs: Date.now() - startTime,
        compressedSize: 0,
        uncompressedSize: 0,
        status: 'ready' as const,
        engineVersion: ENGINE_VERSION,
        repoCount: preset.repoIds.length,
        commitCount: snapshotGitCommits.length,
        jiraEventCount: snapshotJiraEvents.length,
        confluenceEventCount: snapshotConfluenceEvents.length,
      };

      await presetStore.saveSnapshot(meta, snapshotData);
      presetStore.putInHotCache({ meta, data: snapshotData, hydratedAt: Date.now() });

      // Also save to analysis history store for Saved Analyses tab
      const durationMs = Date.now() - startTime;
      const repoNames = preset.repoIds.map(id => availableRepos.find(r => r.id === id)?.name || id);
      const histSnapId = historyStore.generateSnapshotId();
      historyStore.saveSnapshot(
        {
          id: histSnapId,
          mode: 'combined',
          title: `Combined — ${preset.name}`,
          timestamp: new Date().toISOString(),
          durationMs,
          success: true,
          summary: {
            commitCount: snapshotGitCommits.length,
            eventCount: snapshotJiraEvents.length,
            authorCount: 0,
            repoCount: preset.repoIds.length,
            dateRange: { start: fromDate.toISOString(), end: toDate.toISOString() },
          },
        },
        {
          gitCommits: snapshotGitCommits,
          jiraEvents: snapshotJiraEvents,
          jiraSprintSummary: snapshotJiraSprintSummary,
          selectedRepoNames: repoNames,
          customFrom: toLocalDateStr(fromDate),
          customTo: toLocalDateStr(toDate),
          selectedJiraProject: preset.jiraProjectKey || '',
        } satisfies CombinedReviewData
      ).catch(err => console.error('[analysis-history] Failed to save combined preset snapshot:', err));

      notifyAnalysisComplete({
        mode: 'combined',
        presetName: preset.name,
        commitCount: snapshotGitCommits.length,
        eventCount: snapshotJiraEvents.length,
        durationMs,
        success: true,
      });

      setAnalysisJobs(prev => {
        const next = new Map(prev);
        next.set(preset.id, { presetId: preset.id, status: 'done', progress: 100, message: 'Done' });
        return next;
      });

      // Clear done status after a moment
      setTimeout(() => {
        setAnalysisJobs(prev => {
          const next = new Map(prev);
          next.delete(preset.id);
          return next;
        });
      }, 2000);

    } catch (e: any) {
      notifyAnalysisComplete({
        mode: 'combined',
        presetName: preset.name,
        commitCount: 0,
        eventCount: 0,
        durationMs: Date.now() - startTime,
        success: false,
        error: e.message,
      });
      setAnalysisJobs(prev => {
        const next = new Map(prev);
        next.set(preset.id, { presetId: preset.id, status: 'failed', progress: 0, message: '', error: e.message });
        return next;
      });
    } finally {
      analysisQueueRef.current.delete(preset.id);
    }
  }, [availableRepos, storedUsername, storedToken, hasJira, jiraDomain, jiraEmail, jiraApiToken, defaultBoardId]);

  const applySnapshot = useCallback((preset: Preset, snapshot: HydratedSnapshot | null) => {
    setActivePreset(preset);

    // Always sync selector state with preset config
    setSelectedRepoIds(new Set(preset.repoIds));
    if (preset.jiraProjectKey) setSelectedJiraProject(preset.jiraProjectKey);
    if (preset.confluenceSpaceKey) setSelectedConfluenceSpace(preset.confluenceSpaceKey);
    const { from, to } = resolveTimeRange(preset.timeRange);
    setCustomFrom(toLocalDateStr(from));
    setCustomTo(toLocalDateStr(to));

    if (snapshot) {
      // Hydrate the view from cached snapshot
      setGitCommits(snapshot.data.gitCommits || []);
      setJiraEvents(snapshot.data.jiraEvents || []);
      setJiraSprintSummary(snapshot.data.jiraSprintSummary || []);
      setConfluenceEvents((snapshot.data.confluenceEvents || []) as ConfluencePageEvent[]);
      if (snapshot.data.selectedConfluenceSpace) setSelectedConfluenceSpace(snapshot.data.selectedConfluenceSpace);
      setDataReady(true);
      setShowSelector(false);
      const today = toLocalDateStr();
      const yesterday = toLocalDateStr(new Date(Date.now() - 86400000));
      setExpandedDates(new Set([today, yesterday]));
    } else {
      // No snapshot — clear data, show selector so user can run analysis
      setDataReady(false);
      setGitCommits([]);
      setJiraEvents([]);
      setJiraSprintSummary([]);
      setConfluenceEvents([]);
      setShowSelector(true);
    }
  }, []);

  const handleOpenPreset = useCallback((preset: Preset, snapshot: HydratedSnapshot | null) => {
    applySnapshot(preset, snapshot);
    refreshPresets();
  }, [applySnapshot, refreshPresets]);

  // Quick-switch: load from hot/warm cache and swap view
  const handleSwitchPreset = useCallback(async (preset: Preset) => {
    if (activePreset?.id === preset.id) return;
    const snapshot = await presetStore.loadSnapshot(preset.id);
    applySnapshot(preset, snapshot);
  }, [activePreset?.id, applySnapshot]);

  const handleBackToPresets = useCallback(() => {
    setActivePreset(null);
    setDataReady(false);
    setGitCommits([]);
    setJiraEvents([]);
    setJiraSprintSummary([]);
    setConfluenceEvents([]);
    setNotionEvents([]);
    setGoogleDocEvents([]);
    setShowSelector(true);
    refreshPresets();
  }, [refreshPresets]);

  // Keyboard shortcuts: Cmd+1..9 to switch presets, Cmd+0 back to grid
  usePresetShortcuts({
    presets: allPresets,
    activePresetId: activePreset?.id ?? null,
    onSwitchPreset: handleSwitchPreset,
    onBackToGrid: handleBackToPresets,
  });

  // Auto-save snapshot when manual analysis completes while a preset is active
  const snapshotSaveRef = useRef(false);
  useEffect(() => {
    if (!dataReady || !activePreset || snapshotSaveRef.current) return;
    if (gitCommits.length === 0 && jiraEvents.length === 0 && confluenceEvents.length === 0) return;
    snapshotSaveRef.current = true;

    const saveAsync = async () => {
      try {
        const snapshotData: SnapshotData = {
          gitCommits,
          jiraEvents,
          jiraSprintSummary,
          confluenceEvents,
          notionEvents,
          googleDocEvents,
          selectedConfluenceSpace,
        };
        const meta = {
          id: crypto.randomUUID(),
          presetId: activePreset.id,
          resolvedFrom: new Date(customFrom).toISOString(),
          resolvedTo: new Date(customTo).toISOString(),
          analysedAt: new Date().toISOString(),
          durationMs: 0,
          compressedSize: 0,
          uncompressedSize: 0,
          status: 'ready' as const,
          engineVersion: ENGINE_VERSION,
          repoCount: selectedRepoIds.size,
          commitCount: gitCommits.length,
          jiraEventCount: jiraEvents.length,
          confluenceEventCount: confluenceEvents.length,
          notionEventCount: notionEvents.length,
          googleDocEventCount: googleDocEvents.length,
        };
        await presetStore.saveSnapshot(meta, snapshotData);
        presetStore.putInHotCache({ meta, data: snapshotData, hydratedAt: Date.now() });
        console.log(`[presets] Snapshot saved for "${activePreset.name}" (${gitCommits.length} commits, ${jiraEvents.length} events)`);
      } catch (err) {
        console.error('[presets] Failed to save snapshot:', err);
      }
    };
    saveAsync();
  }, [dataReady, activePreset, gitCommits, jiraEvents, jiraSprintSummary, confluenceEvents, notionEvents, googleDocEvents, selectedConfluenceSpace, customFrom, customTo, selectedRepoIds.size]);

  // Reset save flag when preset changes
  useEffect(() => { snapshotSaveRef.current = false; }, [activePreset?.id]);

  // ==================== PDF DOWNLOAD ====================
  const [pdfDownloading, setPdfDownloading] = useState(false);

  // ==================== USER MATCHING ====================
  // Build unified user profiles matching git emails/names to Jira display names
  const gitAliases = useMemo(() => getStoredGitAliases(), []);

  const userProfiles = useMemo(() => {
    const profiles = new Map<string, UserProfile>();

    // Helper to find or create a profile for a name
    const getOrCreate = (name: string, email?: string): UserProfile => {
      const normalized = normalizeNameForMatch(name);
      // Try to match by normalized name
      for (const [, profile] of profiles) {
        if (normalizeNameForMatch(profile.name) === normalized) return profile;
        if (email && profile.emails.has(email.toLowerCase())) return profile;
        // Match first name + last initial patterns
        const nameParts = normalized.split(' ');
        const profileParts = normalizeNameForMatch(profile.name).split(' ');
        if (nameParts.length > 0 && profileParts.length > 0 && nameParts[0] === profileParts[0] && nameParts[0].length > 2) {
          // Same first name — check if last names start the same
          if ((nameParts[1] || '')[0] === (profileParts[1] || '')[0]) return profile;
        }
      }
      // Try email match
      if (email) {
        for (const [, profile] of profiles) {
          if (profile.emails.has(email.toLowerCase())) return profile;
        }
      }
      // Create new
      const profile: UserProfile = { name, emails: new Set(), gitCommits: 0, jiraEvents: 0, additions: 0, deletions: 0 };
      if (email) profile.emails.add(email.toLowerCase());
      profiles.set(normalized, profile);
      return profile;
    };

    // Process git commits — resolve alias FIRST so "will" → "Will Kellner" etc.
    for (const commit of gitCommits) {
      const resolvedName = resolveGitAuthor(commit.author, gitAliases);
      const profile = getOrCreate(resolvedName, commit.email);
      if (commit.email) profile.emails.add(commit.email.toLowerCase());
      profile.gitCommits++;
      profile.additions += commit.additions || 0;
      profile.deletions += commit.deletions || 0;
    }

    // Process Jira events — also resolve in case Jira display names need aliasing
    for (const event of jiraEvents) {
      const resolvedName = resolveGitAuthor(event.author.displayName, gitAliases);
      const profile = getOrCreate(resolvedName);
      if (event.author.accountId) profile.jiraAccountId = event.author.accountId;
      if (event.author.avatarUrl) profile.jiraAvatarUrl = event.author.avatarUrl;
      profile.jiraEvents++;
    }

    // Process Notion events
    for (const event of notionEvents) {
      const resolvedName = resolveGitAuthor(event.lastEditedBy?.name || 'Unknown', gitAliases);
      const profile = getOrCreate(resolvedName);
      if (event.lastEditedBy?.id) profile.jiraAccountId = event.lastEditedBy.id;
      if (event.lastEditedBy?.avatarUrl) profile.jiraAvatarUrl = event.lastEditedBy.avatarUrl;
      // Note: We don't have separate counters for Notion/Google Docs in UserProfile yet
    }

    // Process Google Docs events
    for (const event of googleDocEvents) {
      const resolvedName = resolveGitAuthor(event.authorName || 'Unknown', gitAliases);
      getOrCreate(resolvedName);
      // Note: We don't have separate counters for Notion/Google Docs in UserProfile yet
    }

    return Array.from(profiles.values()).sort((a, b) => (b.gitCommits + b.jiraEvents) - (a.gitCommits + a.jiraEvents));
  }, [gitCommits, jiraEvents, gitAliases]);

  // ==================== LINKED ITEMS (Git ↔ Jira matching) ====================

  const [expandedLinkedKeys, setExpandedLinkedKeys] = useState<Set<string>>(new Set());
  const [showLinkedSection, setShowLinkedSection] = useState(false);
  const [linkedCommitLimit, setLinkedCommitLimit] = useState<Map<string, number>>(new Map());
  const [linkedCommitsCollapsed, setLinkedCommitsCollapsed] = useState<Set<string>>(new Set());

  const linkedItems = useMemo((): LinkedGroup[] => {
    if (gitCommits.length === 0 || jiraEvents.length === 0) return [];

    const jiraByKey = new Map<string, JiraEvent[]>();
    for (const event of jiraEvents) {
      const key = event.issueKey.toUpperCase();
      if (!jiraByKey.has(key)) jiraByKey.set(key, []);
      jiraByKey.get(key)!.push(event);
    }

    const groups = new Map<string, LinkedGroup>();
    for (const commit of gitCommits) {
      const keysFromMsg = extractJiraKeys(commit.message);
      const keysFromBranch = extractJiraKeys(commit.branch);
      const allKeys = [...new Set([...keysFromMsg, ...keysFromBranch])];

      for (const key of allKeys) {
        if (!jiraByKey.has(key)) continue;
        if (!groups.has(key)) {
          const events = jiraByKey.get(key)!;
          const first = events[0];
          const statusEvents = events.filter(e => e.type === 'status_change' && e.toStatus);
          const latestStatus = statusEvents.length > 0
            ? statusEvents.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())[0].toStatus
            : undefined;
          groups.set(key, { issueKey: key, issueSummary: first.issueSummary, issueType: first.issueType, latestStatus, commits: [], jiraEvents: events });
        }
        if (!groups.get(key)!.commits.some(c => c.hash === commit.hash)) {
          groups.get(key)!.commits.push(commit);
        }
      }
    }

    return Array.from(groups.values()).sort((a, b) => {
      const latestA = Math.max(...a.commits.map(c => new Date(c.date).getTime()), ...a.jiraEvents.map(e => new Date(e.timestamp).getTime()));
      const latestB = Math.max(...b.commits.map(c => new Date(c.date).getTime()), ...b.jiraEvents.map(e => new Date(e.timestamp).getTime()));
      return latestB - latestA;
    });
  }, [gitCommits, jiraEvents]);

  const filteredLinkedItems = useMemo((): LinkedGroup[] => {
    if (!filterUser) return linkedItems;
    const selectedProfile = userProfiles.find(p => p.name === filterUser);
    const profileNameNorm = selectedProfile ? normalizeNameForMatch(selectedProfile.name) : null;
    return linkedItems
      .map(group => {
        const matchingCommits = group.commits.filter(c => {
          const authorNorm = normalizeNameForMatch(resolveGitAuthor(c.author, gitAliases));
          if (profileNameNorm && authorNorm === profileNameNorm) return true;
          if (selectedProfile && c.email && selectedProfile.emails.has(c.email.toLowerCase())) return true;
          if (!selectedProfile) return normalizeNameForMatch(c.author).includes(normalizeNameForMatch(filterUser));
          return false;
        });
        return matchingCommits.length > 0 ? { ...group, commits: matchingCommits } : null;
      })
      .filter((g): g is LinkedGroup => g !== null);
  }, [linkedItems, filterUser, userProfiles, gitAliases]);

  const toggleLinkedKey = useCallback((key: string) => {
    setExpandedLinkedKeys(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  // ==================== UNIFIED TIMELINE ====================
  const timeline = useMemo(() => {
    const items: TimelineItem[] = [];

    // Add git commits
    for (const commit of gitCommits) {
      if (hideMerges && commit.isMerge) continue;
      items.push({
        id: `git-${commit.hash}`,
        timestamp: commit.date,
        source: 'git',
        userName: resolveGitAuthor(commit.author, gitAliases),
        userEmail: commit.email,
        gitCommit: commit,
      });
    }

    // Add Jira events
    for (const event of jiraEvents) {
      if (!showJiraStatus && event.type === 'status_change') continue;
      if (!showJiraAssignments && event.type === 'assignment') continue;
      if (!showJiraSprints && event.type === 'sprint_change') continue;
      if (!showJiraCreated && event.type === 'created') continue;
      if (!showJiraComments && event.type === 'comment') continue;
      items.push({
        id: `jira-${event.id}`,
        timestamp: event.timestamp,
        source: 'jira',
        userName: resolveGitAuthor(event.author.displayName, gitAliases),
        jiraEvent: event,
      });
    }

    // Add Confluence page events
    for (const event of confluenceEvents) {
      items.push({
        id: `confluence-${event.id}`,
        timestamp: event.timestamp,
        source: 'confluence',
        userName: event.authorName || event.authorId || 'Unknown',
        confluenceEvent: event,
      });
    }

    // Add Notion events
    for (const event of notionEvents) {
      items.push({
        id: `notion-${event.id}`,
        timestamp: event.lastEditedTime,
        source: 'notion',
        userName: event.lastEditedBy?.name || 'Unknown',
        userEmail: event.lastEditedBy?.id,
        notionEvent: event,
      });
    }

    // Add Google Docs events
    for (const event of googleDocEvents) {
      items.push({
        id: `google-docs-${event.id}`,
        timestamp: event.timestamp || event.modifiedTime,
        source: 'google-docs',
        userName: event.authorName || event.lastModifyingUser?.displayName || 'Unknown',
        userEmail: event.authorEmail || event.lastModifyingUser?.emailAddress,
        googleDocEvent: event,
      });
    }

    // Apply filters
    let filtered = items;
    if (filterSource !== 'all') filtered = filtered.filter(i => i.source === filterSource);
    if (!showGitCommits) filtered = filtered.filter(i => i.source !== 'git');
    filtered = filtered.filter(i => i.source !== 'confluence' || confluenceEvents.length > 0);
    filtered = filtered.filter(i => i.source !== 'notion' || notionEvents.length > 0);
    filtered = filtered.filter(i => i.source !== 'google-docs' || googleDocEvents.length > 0);
    if (filterUser) {
      // Find the canonical profile for the selected user
      const selectedProfile = userProfiles.find(p => p.name === filterUser);
      if (selectedProfile) {
        // Match any item whose resolved userName matches the profile name,
        // or whose email is in the profile's email set
        const profileNameNorm = normalizeNameForMatch(selectedProfile.name);
        filtered = filtered.filter(i => {
          if (normalizeNameForMatch(i.userName) === profileNameNorm) return true;
          if (i.userEmail && selectedProfile.emails.has(i.userEmail.toLowerCase())) return true;
          return false;
        });
      } else {
        // Fallback: fuzzy substring match
        const q = normalizeNameForMatch(filterUser);
        filtered = filtered.filter(i => normalizeNameForMatch(i.userName).includes(q));
      }
    }
    if (filterSearch) {
      const q = filterSearch.toLowerCase();
      filtered = filtered.filter(i => {
        if (i.gitCommit) return i.gitCommit.message.toLowerCase().includes(q) || i.gitCommit.hash.startsWith(q) || i.gitCommit.author.toLowerCase().includes(q);
        if (i.jiraEvent) return i.jiraEvent.issueKey.toLowerCase().includes(q) || i.jiraEvent.issueSummary.toLowerCase().includes(q);
        if (i.confluenceEvent) return i.confluenceEvent.pageTitle.toLowerCase().includes(q) || i.confluenceEvent.spaceKey.toLowerCase().includes(q);
        return false;
      });
    }

    // Jira-style filters
    if (filterStatus) {
      filtered = filtered.filter(i => {
        if (i.jiraEvent) {
          if (i.jiraEvent.type === 'status_change' && i.jiraEvent.toStatus === filterStatus) return true;
          // Keep events whose issue has had a status_change to this status
          const issueKey = i.jiraEvent.issueKey;
          return jiraEvents.some(ev => ev.issueKey === issueKey && ev.type === 'status_change' && ev.toStatus === filterStatus);
        }
        // For git commits, check if a linked Jira issue has the status
        if (i.gitCommit) {
          const keys = extractJiraKeys(i.gitCommit.message + ' ' + i.gitCommit.branch);
          return keys.some(k => jiraEvents.some(ev => ev.issueKey.toUpperCase() === k && ev.type === 'status_change' && ev.toStatus === filterStatus));
        }
        return true;
      });
    }
    if (filterIssueType) {
      filtered = filtered.filter(i => {
        if (i.jiraEvent) return i.jiraEvent.issueType === filterIssueType;
        if (i.gitCommit) {
          const keys = extractJiraKeys(i.gitCommit.message + ' ' + i.gitCommit.branch);
          return keys.some(k => jiraEvents.some(ev => ev.issueKey.toUpperCase() === k && ev.issueType === filterIssueType));
        }
        return true;
      });
    }
    if (filterSprint) {
      filtered = filtered.filter(i => {
        if (i.jiraEvent) {
          if (i.jiraEvent.type === 'sprint_change' && i.jiraEvent.toSprint === filterSprint) return true;
          return jiraEvents.some(ev => ev.issueKey === i.jiraEvent!.issueKey && ev.type === 'sprint_change' && ev.toSprint === filterSprint);
        }
        if (i.gitCommit) {
          const keys = extractJiraKeys(i.gitCommit.message + ' ' + i.gitCommit.branch);
          return keys.some(k => jiraEvents.some(ev => ev.issueKey.toUpperCase() === k && ev.type === 'sprint_change' && ev.toSprint === filterSprint));
        }
        return true;
      });
    }

    if (filterLabel) {
      filtered = filtered.filter(i => {
        if (i.jiraEvent) {
          if (i.jiraEvent.labels && i.jiraEvent.labels.includes(filterLabel)) return true;
          // Check if the issue has this label on any of its events
          const issueKey = i.jiraEvent.issueKey;
          return jiraEvents.some(ev => ev.issueKey === issueKey && ev.labels && ev.labels.includes(filterLabel));
        }
        if (i.gitCommit) {
          const keys = extractJiraKeys(i.gitCommit.message + ' ' + i.gitCommit.branch);
          return keys.some(k => jiraEvents.some(ev => ev.issueKey.toUpperCase() === k && ev.labels && ev.labels.includes(filterLabel)));
        }
        return true;
      });
    }

    // Git-specific filters
    if (filterRepo) {
      filtered = filtered.filter(i => {
        if (i.gitCommit) return i.gitCommit.repoName === filterRepo;
        return true; // keep Jira events when filtering by repo
      });
    }
    if (filterBranch) {
      filtered = filtered.filter(i => {
        if (i.gitCommit) return i.gitCommit.branch === filterBranch;
        return true;
      });
    }

    // Confluence-specific filters
    if (filterConfluenceEventTypes.size > 0) {
      filtered = filtered.filter(i => {
        if (i.source !== 'confluence') return true;
        const key = toConfluenceEventKey(i.confluenceEvent!);
        return filterConfluenceEventTypes.has(key);
      });
    }
    if (filterConfluenceSpace) {
      filtered = filtered.filter(i => {
        if (i.source !== 'confluence') return true;
        return i.confluenceEvent?.spaceKey === filterConfluenceSpace;
      });
    }

    // Combined-specific: show only linked items
    if (showLinkedOnly) {
      const linkedKeySet = new Set(linkedItems.map(g => g.issueKey));
      filtered = filtered.filter(i => {
        if (i.gitCommit) {
          const keys = extractJiraKeys(i.gitCommit.message + ' ' + i.gitCommit.branch);
          return keys.some(k => linkedKeySet.has(k));
        }
        if (i.jiraEvent) return linkedKeySet.has(i.jiraEvent.issueKey.toUpperCase());
        return false;
      });
    }

    // Combined-specific: show only unlinked items
    if (showUnlinkedOnly) {
      const linkedKeySet = new Set(linkedItems.map(g => g.issueKey));
      filtered = filtered.filter(i => {
        if (i.gitCommit) {
          const keys = extractJiraKeys(i.gitCommit.message + ' ' + i.gitCommit.branch);
          return keys.length === 0 || !keys.some(k => linkedKeySet.has(k));
        }
        if (i.jiraEvent) return !linkedKeySet.has(i.jiraEvent.issueKey.toUpperCase());
        return true;
      });
    }

    // Sort newest first
    filtered.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
    return filtered;
  }, [gitCommits, jiraEvents, confluenceEvents, gitAliases, filterSource, filterUser, filterSearch, filterStatus, filterLabel, filterIssueType, filterSprint, filterRepo, filterBranch, showLinkedOnly, showUnlinkedOnly, showGitCommits, showJiraStatus, showJiraAssignments, showJiraSprints, showJiraCreated, showJiraComments, hideMerges, userProfiles, linkedItems, filterConfluenceEventTypes, filterConfluenceSpace]);

  // Group by date
  const groupedByDate = useMemo(() => {
    const groups = new Map<string, TimelineItem[]>();
    for (const item of timeline) {
      const date = toLocalDateStr(new Date(item.timestamp));
      if (!groups.has(date)) groups.set(date, []);
      groups.get(date)!.push(item);
    }
    return Array.from(groups.entries()).sort((a, b) => b[0].localeCompare(a[0]));
  }, [timeline]);

  const toggleDate = (date: string) => {
    setExpandedDates(prev => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date); else next.add(date);
      return next;
    });
  };

  const toggleTicket = (key: string) => {
    setExpandedTickets(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  // ==================== WATERFALL DATA ====================
  const combinedWaterfallEvents: WaterfallEvent[] = useMemo(() => {
    return timeline.map(item => {
      if (item.gitCommit) {
        // For git commits, try to extract a Jira key from message/branch for the issueKey
        const keys = extractJiraKeys(item.gitCommit.message + ' ' + item.gitCommit.branch);
        return {
          id: item.id,
          timestamp: item.timestamp,
          issueKey: keys[0] || item.gitCommit.repoName + '/' + item.gitCommit.branch,
          issueSummary: item.gitCommit.message.split('\n')[0].substring(0, 80),
          type: 'commit',
          author: item.userName,
          platform: 'git' as const,
          detail: `+${item.gitCommit.additions} -${item.gitCommit.deletions}`,
        };
      }
      if (item.jiraEvent) {
        return {
          id: item.id,
          timestamp: item.timestamp,
          issueKey: item.jiraEvent.issueKey,
          issueSummary: item.jiraEvent.issueSummary,
          type: item.jiraEvent.type,
          author: item.userName,
          platform: 'jira' as const,
          issueType: item.jiraEvent.issueType,
          detail: item.jiraEvent.fromStatus && item.jiraEvent.toStatus
            ? `${item.jiraEvent.fromStatus} → ${item.jiraEvent.toStatus}`
            : item.jiraEvent.toAssignee ? `→ ${item.jiraEvent.toAssignee}` : undefined,
        };
      }
      return {
        id: item.id,
        timestamp: item.timestamp,
        issueKey: 'unknown',
        issueSummary: '',
        type: 'field_change',
        author: item.userName,
        platform: 'jira' as const,
      };
    });
  }, [timeline]);

  // Group by ticket: collapse Jira events per issueKey + keep git commits separate
  interface CombinedTicketGroup {
    issueKey: string;
    issueSummary: string;
    issueType: string;
    latestStatus?: string;
    jiraEvents: JiraEvent[];
    gitCommits: GitCommitItem[];
    latestTimestamp: string;
  }

  const combinedTicketGroups = useMemo((): CombinedTicketGroup[] => {
    if (!groupByTicket) return [];
    const map = new Map<string, CombinedTicketGroup>();

    // Group Jira events by issueKey
    for (const item of timeline) {
      if (item.source === 'jira' && item.jiraEvent) {
        const ev = item.jiraEvent;
        if (!map.has(ev.issueKey)) {
          map.set(ev.issueKey, {
            issueKey: ev.issueKey,
            issueSummary: ev.issueSummary,
            issueType: ev.issueType,
            jiraEvents: [],
            gitCommits: [],
            latestTimestamp: ev.timestamp,
          });
        }
        const group = map.get(ev.issueKey)!;
        group.jiraEvents.push(ev);
        if (new Date(ev.timestamp) > new Date(group.latestTimestamp)) group.latestTimestamp = ev.timestamp;
        if (ev.type === 'status_change' && ev.toStatus) group.latestStatus = ev.toStatus;
      }
      if (item.source === 'git' && item.gitCommit) {
        const commit = item.gitCommit;
        const keys = extractJiraKeys(commit.message + ' ' + commit.branch);
        for (const key of keys) {
          if (map.has(key)) {
            map.get(key)!.gitCommits.push(commit);
          }
        }
      }
    }

    return Array.from(map.values()).sort((a, b) => new Date(b.latestTimestamp).getTime() - new Date(a.latestTimestamp).getTime());
  }, [timeline, groupByTicket]);

  // Git commits not linked to any grouped ticket
  const ungroupedGitCommits = useMemo((): TimelineItem[] => {
    if (!groupByTicket) return [];
    const groupedKeys = new Set(combinedTicketGroups.map(g => g.issueKey));
    return timeline.filter(item => {
      if (item.source !== 'git' || !item.gitCommit) return false;
      const keys = extractJiraKeys(item.gitCommit.message + ' ' + item.gitCommit.branch);
      return !keys.some(k => groupedKeys.has(k));
    });
  }, [timeline, groupByTicket, combinedTicketGroups]);

  // ==================== SIDEBAR STATS ====================

  const statusBreakdown = useMemo(() => {
    const counts = new Map<string, number>();
    for (const ev of jiraEvents) {
      if (ev.type === 'status_change' && ev.toStatus) {
        counts.set(ev.toStatus, (counts.get(ev.toStatus) || 0) + 1);
      }
    }
    return Array.from(counts.entries()).map(([status, count]) => ({ status, count })).sort((a, b) => b.count - a.count);
  }, [jiraEvents]);

  const issueTypeBreakdown = useMemo(() => {
    const counts = new Map<string, number>();
    const seen = new Set<string>();
    for (const ev of jiraEvents) {
      const k = ev.issueKey + ev.issueType;
      if (!seen.has(k)) { seen.add(k); counts.set(ev.issueType, (counts.get(ev.issueType) || 0) + 1); }
    }
    return Array.from(counts.entries()).map(([type, count]) => ({ type, count })).sort((a, b) => b.count - a.count);
  }, [jiraEvents]);

  const labelBreakdown = useMemo(() => {
    const counts = new Map<string, number>();
    const seen = new Set<string>();
    for (const ev of jiraEvents) {
      if (ev.labels && ev.labels.length > 0) {
        const seenKey = ev.issueKey;
        for (const label of ev.labels) {
          const k = seenKey + '::' + label;
          if (!seen.has(k)) { seen.add(k); counts.set(label, (counts.get(label) || 0) + 1); }
        }
      }
    }
    return Array.from(counts.entries()).map(([label, count]) => ({ label, count })).sort((a, b) => b.count - a.count);
  }, [jiraEvents]);

  const sprintBreakdown = useMemo(() => {
    if (jiraSprintSummary.length === 0) return [];
    const active = jiraSprintSummary.filter((s: any) => s.state === 'active' || s.state === 'future');
    const closed = jiraSprintSummary.filter((s: any) => s.state !== 'active' && s.state !== 'future').slice(-3).reverse();
    return [...active, ...closed];
  }, [jiraSprintSummary]);

  const repoBreakdown = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of gitCommits) { counts.set(c.repoName, (counts.get(c.repoName) || 0) + 1); }
    return Array.from(counts.entries()).map(([repo, count]) => ({ repo, count })).sort((a, b) => b.count - a.count);
  }, [gitCommits]);

  const branchBreakdown = useMemo(() => {
    const counts = new Map<string, number>();
    const latestDate = new Map<string, string>();
    for (const c of gitCommits) {
      counts.set(c.branch, (counts.get(c.branch) || 0) + 1);
      const prev = latestDate.get(c.branch);
      if (!prev || c.date > prev) latestDate.set(c.branch, c.date);
    }
    return Array.from(counts.entries())
      .map(([branch, count]) => ({ branch, count, lastUpdate: latestDate.get(branch) || '' }))
      .sort((a, b) => new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime());
  }, [gitCommits]);

  // ==================== CONFLUENCE BREAKDOWNS ====================

  function toConfluenceEventKey(ev: ConfluencePageEvent) {
    if (ev.eventType === 'comment_added') return ev.commentType === 'inline' ? 'inline_comment' : 'footer_comment';
    return ev.eventType ?? 'page_edited';
  }

  const CONFLUENCE_EVENT_LABELS: Record<string, { label: string; color: string }> = {
    page_created: { label: 'New Page', color: 'emerald' },
    page_edited: { label: 'Page Edited', color: 'sky' },
    minor_edit: { label: 'Minor Edit', color: 'slate' },
    footer_comment: { label: 'Footer Comment', color: 'purple' },
    inline_comment: { label: 'Inline Comment', color: 'violet' },
  };

  const confluenceEventTypeBreakdown = useMemo(() => {
    const counts = new Map<string, number>();
    for (const ev of confluenceEvents) {
      const key = toConfluenceEventKey(ev);
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    const order = ['page_created', 'page_edited', 'minor_edit', 'footer_comment', 'inline_comment'];
    return order.filter(k => counts.has(k)).map(k => ({ key: k, count: counts.get(k)! }));
  }, [confluenceEvents]);

  const confluenceSpaceBreakdown = useMemo(() => {
    const counts = new Map<string, { key: string; name: string; count: number }>();
    for (const ev of confluenceEvents) {
      if (!counts.has(ev.spaceKey)) counts.set(ev.spaceKey, { key: ev.spaceKey, name: ev.spaceName || ev.spaceKey, count: 0 });
      counts.get(ev.spaceKey)!.count++;
    }
    return Array.from(counts.values()).sort((a, b) => b.count - a.count);
  }, [confluenceEvents]);

  // ==================== CHART DATA ====================
  const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#a855f7', '#f43f5e', '#06b6d4', '#f97316', '#ec4899', '#6366f1', '#14b8a6'];

  const dailyActivityData = useMemo(() => {
    const dayMap = new Map<string, { date: string; _ts: number; git: number; jira: number }>();
    for (const c of gitCommits) {
      const d = new Date(c.date);
      const key = toLocalDateStr(d);
      if (!dayMap.has(key)) dayMap.set(key, { date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), _ts: d.setHours(0, 0, 0, 0), git: 0, jira: 0 });
      dayMap.get(key)!.git++;
    }
    for (const e of jiraEvents) {
      const d = new Date(e.timestamp);
      const key = toLocalDateStr(d);
      if (!dayMap.has(key)) dayMap.set(key, { date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), _ts: d.setHours(0, 0, 0, 0), git: 0, jira: 0 });
      dayMap.get(key)!.jira++;
    }
    return Array.from(dayMap.values()).sort((a, b) => a._ts - b._ts).map(({ _ts, ...rest }) => rest);
  }, [gitCommits, jiraEvents]);

  const userActivityData = useMemo(() => {
    return userProfiles
      .map(p => ({ name: p.name.length > 12 ? p.name.split(' ')[0] : p.name, fullName: p.name, gitCommits: p.gitCommits, jiraEvents: p.jiraEvents }))
      .sort((a, b) => (b.gitCommits + b.jiraEvents) - (a.gitCommits + a.jiraEvents));
  }, [userProfiles]);

  const sourceShareData = useMemo(() => {
    return [
      { name: 'Git Commits', value: gitCommits.length, fill: '#f97316' },
      { name: 'Jira Events', value: jiraEvents.length, fill: '#3b82f6' },
    ].filter(d => d.value > 0);
  }, [gitCommits, jiraEvents]);

  const statusChartData = useMemo(() => {
    return statusBreakdown.slice(0, 10).map((s, i) => ({ name: s.status, count: s.count, fill: CHART_COLORS[i % CHART_COLORS.length] }));
  }, [statusBreakdown]);

  const issueTypeChartData = useMemo(() => {
    return issueTypeBreakdown.map((t, i) => ({ name: t.type, count: t.count, fill: CHART_COLORS[i % CHART_COLORS.length] }));
  }, [issueTypeBreakdown]);

  const repoChartData = useMemo(() => {
    return repoBreakdown.map((r, i) => ({ name: r.repo.length > 15 ? r.repo.substring(0, 13) + '..' : r.repo, fullName: r.repo, commits: r.count, fill: CHART_COLORS[i % CHART_COLORS.length] }));
  }, [repoBreakdown]);

  // ==================== DIFF LOGIC ====================

  const fetchDiff = useCallback(async (commit: GitCommitItem) => {
    setDiffLoading(true);
    setDiff('');
    setExpandedDiffFiles(new Set());
    try {
      const repo = availableRepos.find(r => r.id === commit.repoId);
      if (!repo) { setDiff('Could not find repository configuration.'); return; }
      const config: any = { type: repo.type, url: repo.url, path: repo.path, name: repo.name, hash: commit.hash };
      if (storedToken && (repo.type === 'bitbucket' || repo.type === 'github')) {
        config.username = storedUsername || '';
        config.appPassword = storedToken;
      }
      const diffResult = await api.getCommitDiff(config, commit.hash);
      setDiff(diffResult.diff || 'No diff available');
    } catch {
      setDiff('Error loading diff');
    } finally {
      setDiffLoading(false);
    }
  }, [availableRepos, storedUsername, storedToken]);

  const handleItemClick = useCallback((item: TimelineItem) => {
    const isSelected = selectedItemId === item.id;
    if (isSelected) {
      setSelectedItemId(null);
      setDiff('');
      setExpandedDiffFiles(new Set());
    } else {
      setSelectedItemId(item.id);
      if (item.source === 'git' && item.gitCommit) {
        fetchDiff(item.gitCommit);
      }
    }
  }, [selectedItemId, fetchDiff]);

  const parsedDiffFiles = useMemo(() => {
    if (!diff) return [];
    const files: { filename: string; additions: number; deletions: number; lines: string[] }[] = [];
    const rawLines = diff.split('\n');
    let current: typeof files[0] | null = null;
    for (const line of rawLines) {
      if (line.startsWith('diff --git')) {
        const match = line.match(/diff --git a\/(.*?) b\/(.*)/);
        const filename = match ? match[2] : line.replace('diff --git ', '');
        current = { filename, additions: 0, deletions: 0, lines: [] };
        files.push(current);
        current.lines.push(line);
      } else if (current) {
        current.lines.push(line);
        if (line.startsWith('+') && !line.startsWith('+++')) current.additions++;
        else if (line.startsWith('-') && !line.startsWith('---')) current.deletions++;
      }
    }
    return files;
  }, [diff]);

  const toggleDiffFile = useCallback((idx: number) => {
    setExpandedDiffFiles(prev => { const next = new Set(prev); if (next.has(idx)) next.delete(idx); else next.add(idx); return next; });
  }, []);

  const expandAllDiffFiles = useCallback(() => { setExpandedDiffFiles(new Set(parsedDiffFiles.map((_, i) => i))); }, [parsedDiffFiles]);
  const collapseAllDiffFiles = useCallback(() => { setExpandedDiffFiles(new Set()); }, []);

  // Repo color mapping
  const repoColorMap = useMemo(() => {
    const map = new Map<string, typeof PROJECT_COLORS[0]>();
    const arr = Array.from(selectedRepoIds);
    arr.forEach((id, i) => map.set(id, PROJECT_COLORS[i % PROJECT_COLORS.length]));
    return map;
  }, [selectedRepoIds]);

  const loading = gitLoading || jiraLoading;
  const totalGit = gitCommits.length;
  const totalJira = jiraEvents.length;

  // ==================== RENDER ====================

  const renderTimelineItem = (item: TimelineItem) => {
    const isSelected = selectedItemId === item.id;

    if (item.source === 'git' && item.gitCommit) {
      const commit = item.gitCommit;
      const color = repoColorMap.get(commit.repoId) || PROJECT_COLORS[0];
      return (
        <div key={item.id}>
          <div
            onClick={() => handleItemClick(item)}
            className={`flex items-start gap-3 px-4 py-2.5 hover:bg-slate-700/10 transition-colors border-b border-slate-700/10 last:border-b-0 cursor-pointer ${isSelected ? 'bg-orange-500/5' : ''}`}>
            <div className="mt-0.5 flex-shrink-0 flex items-center gap-1 text-orange-400">
              {isSelected ? <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3 text-slate-500" /> : <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3 text-slate-500" />}
              <FontAwesomeIcon icon={faCodeCommit} className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${color.light} ${color.text} border ${color.border}`}>{commit.repoName}</span>
                <span className="text-xs text-slate-300 truncate max-w-[400px]">{commit.message.split('\n')[0]}</span>
              </div>
              <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500">
                <span className="font-mono">{commit.hash.slice(0, 7)}</span>
                <span>{commit.branch}</span>
                {(commit.additions > 0 || commit.deletions > 0) && (
                  <span>
                    <span className="text-green-400">+{commit.additions}</span>
                    {' / '}
                    <span className="text-red-400">-{commit.deletions}</span>
                  </span>
                )}
              </div>
            </div>
            <div className="flex-shrink-0 text-right">
              <div className="text-xs text-slate-300 font-medium">{commit.author}</div>
              <div className="text-[10px] text-slate-500">{formatTime(commit.date)}</div>
            </div>
          </div>
          {/* Inline diff accordion */}
          {isSelected && (
            <div className={`border-b border-slate-700/10 ${isDark ? 'bg-slate-950/50' : 'bg-slate-50'}`}>
              {diffLoading ? (
                <div className="flex items-center justify-center gap-2 py-6">
                  <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin text-slate-400" />
                  <span className="text-xs text-slate-400">Loading diff...</span>
                </div>
              ) : parsedDiffFiles.length > 0 ? (
                <div className="p-3 space-y-1">
                  {/* File summary bar */}
                  <div className={`flex items-center justify-between px-3 py-2 rounded-lg mb-2 ${isDark ? 'bg-white/[0.03]' : 'bg-gray-100'}`}>
                    <span className="text-xs font-medium text-slate-400">
                      {parsedDiffFiles.length} file{parsedDiffFiles.length !== 1 ? 's' : ''} changed
                      <span className="ml-2 text-emerald-400">+{parsedDiffFiles.reduce((s, f) => s + f.additions, 0)}</span>
                      <span className="ml-1 text-rose-400">-{parsedDiffFiles.reduce((s, f) => s + f.deletions, 0)}</span>
                    </span>
                    <div className="flex items-center gap-1">
                      <button onClick={(e) => { e.stopPropagation(); expandAllDiffFiles(); }} className="text-[10px] px-2 py-0.5 rounded hover:bg-white/10 text-white/50">Expand all</button>
                      <button onClick={(e) => { e.stopPropagation(); collapseAllDiffFiles(); }} className="text-[10px] px-2 py-0.5 rounded hover:bg-white/10 text-white/50">Collapse all</button>
                    </div>
                  </div>
                  {parsedDiffFiles.map((file, fileIdx) => {
                    const isFileExpanded = expandedDiffFiles.has(fileIdx);
                    const totalChanges = file.additions + file.deletions;
                    const addPct = totalChanges > 0 ? Math.round((file.additions / totalChanges) * 100) : 0;
                    return (
                      <div key={fileIdx} className={`rounded-lg border overflow-hidden ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleDiffFile(fileIdx); }}
                          className={`w-full flex items-center gap-2 px-3 py-2 text-left transition-colors ${isDark ? 'bg-white/[0.03] hover:bg-white/[0.06]' : 'bg-gray-50 hover:bg-gray-100'}`}>
                          {isFileExpanded ? <FontAwesomeIcon icon={faChevronDown} className="h-3.5 w-3.5 flex-shrink-0 text-slate-500" /> : <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5 flex-shrink-0 text-slate-500" />}
                          <FontAwesomeIcon icon={faFile} className={`h-3.5 w-3.5 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                          <span className={`text-xs font-mono truncate flex-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{file.filename}</span>
                          <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                            <span className="text-xs font-mono font-medium text-emerald-400">+{file.additions}</span>
                            <span className="text-xs font-mono font-medium text-rose-400">-{file.deletions}</span>
                            <div className="flex h-2 w-12 rounded-full overflow-hidden bg-gray-700/20 ml-1">
                              <div className="bg-emerald-500" style={{ width: `${addPct}%` }} />
                              <div className="bg-rose-500" style={{ width: `${100 - addPct}%` }} />
                            </div>
                          </div>
                        </button>
                        {isFileExpanded && (
                          <div className="overflow-x-auto">
                            {file.lines.map((line, lineIdx) => {
                              let lineClass = `font-mono text-xs whitespace-pre px-4 py-0.5 ${isDark ? 'text-white/80' : 'text-gray-800'}`;
                              if (line.startsWith('+') && !line.startsWith('+++')) lineClass = `font-mono text-xs whitespace-pre px-4 py-0.5 ${isDark ? 'bg-green-500/10 text-green-300' : 'bg-green-100 text-green-800'}`;
                              else if (line.startsWith('-') && !line.startsWith('---')) lineClass = `font-mono text-xs whitespace-pre px-4 py-0.5 ${isDark ? 'bg-red-500/10 text-red-300' : 'bg-red-100 text-red-800'}`;
                              else if (line.startsWith('@@')) lineClass = `font-mono text-xs whitespace-pre px-4 py-0.5 ${isDark ? 'bg-blue-500/10 text-blue-300' : 'bg-blue-100 text-blue-800'}`;
                              else if (line.startsWith('diff --git') || line.startsWith('index ') || line.startsWith('---') || line.startsWith('+++')) lineClass = `font-mono text-xs whitespace-pre px-4 py-0.5 font-semibold ${isDark ? 'text-white/50 bg-white/[0.02]' : 'text-gray-500 bg-gray-50'}`;
                              return <div key={lineIdx} className={lineClass}>{line || ' '}</div>;
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : diff ? (
                <div className="p-4">
                  <pre className="font-mono text-xs whitespace-pre-wrap text-slate-400">{diff}</pre>
                </div>
              ) : null}
            </div>
          )}
        </div>
      );
    }

    if (item.source === 'jira' && item.jiraEvent) {
      const event = item.jiraEvent;
      const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
        status_change: mkIcon(faArrowRightArrowLeft), assignment: mkIcon(faUserPen), sprint_change: mkIcon(faCodeBranch),
        priority_change: mkIcon(faFlag), created: mkIcon(faPlus), resolved: mkIcon(faCircleCheck), comment: mkIcon(faComment),
      };
      const colorMap: Record<string, string> = {
        status_change: 'text-blue-400', assignment: 'text-purple-400', sprint_change: 'text-cyan-400',
        priority_change: 'text-orange-400', created: 'text-emerald-400', resolved: 'text-green-400',
        comment: 'text-slate-400',
      };
      const Icon = iconMap[event.type] || mkIcon(faBolt);
      const iconColor = colorMap[event.type] || 'text-slate-400';

      return (
        <div key={item.id}>
          <div
            onClick={() => handleItemClick(item)}
            className={`flex items-start gap-3 px-4 py-2.5 hover:bg-slate-700/10 transition-colors border-b border-slate-700/10 last:border-b-0 cursor-pointer ${isSelected ? 'bg-blue-500/5' : ''}`}>
            <div className={`mt-0.5 flex-shrink-0 flex items-center gap-1 ${iconColor}`}>
              {isSelected ? <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3 text-slate-500" /> : <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3 text-slate-500" />}
              <Icon className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <a href={`https://${jiraDomain}.atlassian.net/browse/${event.issueKey}`}
                  target="_blank" rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs font-mono text-blue-400 hover:text-blue-300 font-medium">
                  {event.issueKey}
                </a>
                <span className="text-xs text-slate-300 truncate max-w-[300px]">{event.issueSummary}</span>
                <span className="text-[10px] px-1.5 py-0.5 bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 rounded">{event.issueType}</span>
              </div>
              <div className="mt-1">
                {event.type === 'status_change' && (
                  <div className="flex items-center gap-2 flex-wrap">
                    {event.fromStatus && <span className={`px-2 py-0.5 text-[10px] rounded border ${getStatusColor(event.fromStatus)}`}>{event.fromStatus}</span>}
                    <span className="text-slate-500 text-xs">→</span>
                    {event.toStatus && <span className={`px-2 py-0.5 text-[10px] rounded border ${getStatusColor(event.toStatus)}`}>{event.toStatus}</span>}
                  </div>
                )}
                {event.type === 'assignment' && (
                  <div className="text-xs text-slate-400">
                    <span>{event.fromAssignee || 'Unassigned'}</span>
                    <span className="text-slate-500 mx-1">→</span>
                    <span className="text-purple-300 font-medium">{event.toAssignee || 'Unassigned'}</span>
                  </div>
                )}
                {event.type === 'sprint_change' && (
                  <div className="text-xs text-slate-400">
                    {event.fromSprint && <><span>{event.fromSprint}</span><span className="text-slate-500 mx-1">→</span></>}
                    <span className="text-cyan-300 font-medium">{event.toSprint || 'Removed'}</span>
                  </div>
                )}
                {event.type === 'priority_change' && (
                  <div className="text-xs text-slate-400">{event.fromValue} <span className="text-slate-500">→</span> <span className="text-orange-300 font-medium">{event.toValue}</span></div>
                )}
                {event.type === 'created' && <span className="text-[10px] text-emerald-400">Issue created</span>}
                {event.type === 'resolved' && <span className="text-[10px] text-green-400">Resolved: {event.toValue}</span>}
                {event.type === 'comment' && (
                  <div className="text-[10px] text-slate-400 italic truncate max-w-md">
                    {event.commentBody ? `"${event.commentBody}"` : 'Added a comment'}
                  </div>
                )}
              </div>
            </div>
            <div className="flex-shrink-0 text-right">
              <div className="text-xs text-slate-300 font-medium">{event.author.displayName}</div>
              <div className="text-[10px] text-slate-500">{formatTime(event.timestamp)}</div>
            </div>
          </div>
          {/* Inline Jira issue detail accordion */}
          {isSelected && hasJira && (
            <div className="border-b border-slate-700/10">
              <JiraIssueDetail
                issueKey={event.issueKey}
                jiraDomain={jiraDomain!}
                jiraEmail={jiraEmail!}
                jiraApiToken={jiraApiToken!}
                onClose={() => setSelectedItemId(null)}
                onNavigate={(key) => { setSelectedItemId(null); setTimeout(() => setSelectedItemId('nav-' + key), 50); }}
                relatedCommits={linkedItems.find(g => g.issueKey === event.issueKey)?.commits}
                availableRepos={availableRepos}
                storedUsername={storedUsername}
                storedToken={storedToken}
              />
            </div>
          )}
        </div>
      );
    }

    if (item.source === 'confluence' && item.confluenceEvent) {
      const ev = item.confluenceEvent;
      const isComment = ev.eventType === 'comment_added';
      return (
        <div
          key={item.id}
          onClick={() => setSelectedItemId(isSelected ? null : item.id)}
          className={`group px-4 py-3 cursor-pointer border-b transition-colors ${
            isSelected
              ? isDark ? 'bg-sky-500/5 border-sky-500/10' : 'bg-sky-50/50 border-sky-100'
              : isDark ? 'border-slate-700/20 hover:bg-slate-800/30' : 'border-gray-100 hover:bg-gray-50/50'
          }`}
        >
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 mt-0.5">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                isComment ? (isDark ? 'bg-purple-500/10 text-purple-400' : 'bg-purple-50 text-purple-500') :
                ev.isNew ? (isDark ? 'bg-emerald-500/10 text-emerald-400' : 'bg-emerald-50 text-emerald-500') :
                ev.minorEdit ? (isDark ? 'bg-slate-500/10 text-slate-400' : 'bg-slate-100 text-slate-400') :
                (isDark ? 'bg-sky-500/10 text-sky-400' : 'bg-sky-50 text-sky-500')
              }`}>
                {isComment ? <FontAwesomeIcon icon={faComment} className="h-3.5 w-3.5" /> :
                 ev.isNew ? <FontAwesomeIcon icon={faCirclePlus} className="h-3.5 w-3.5" /> :
                 ev.minorEdit ? <FontAwesomeIcon icon={faPencil} className="h-3.5 w-3.5" /> :
                 <FontAwesomeIcon icon={faPenToSquare} className="h-3.5 w-3.5" />}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <a href={ev.url} target="_blank" rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-xs font-medium text-sky-400 hover:text-sky-300 truncate max-w-[380px]">
                  {ev.pageTitle}
                </a>
                <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${isDark ? 'bg-slate-500/10 text-slate-400 border-slate-500/20' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                  <FontAwesomeIcon icon={faLayerGroup} className="h-2.5 w-2.5 flex-shrink-0" />
                  {ev.spaceName || ev.spaceKey} – Space
                </span>
                {isComment ? (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${isDark ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 'bg-purple-50 text-purple-600 border-purple-200'}`}>
                    {ev.commentType === 'inline' ? 'Inline comment' : 'Footer comment'}
                    {(ev.commentVersion ?? 0) > 1 && ` · v${ev.commentVersion}`}
                  </span>
                ) : ev.isNew ? (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${isDark ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-emerald-50 text-emerald-600 border-emerald-200'}`}>New page</span>
                ) : (
                  <span className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>v{ev.version}</span>
                )}
                {ev.versionMessage && <span className={`text-[10px] truncate max-w-[180px] ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>— {ev.versionMessage}</span>}
              </div>
            </div>
            <div className="flex-shrink-0 text-right">
              <div className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{ev.authorName || ev.authorId}</div>
              <div className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{formatTime(ev.timestamp)}</div>
            </div>
          </div>
          {isSelected && (
            <div className="mt-1">
              {isComment && <CommentEventDetail event={ev} />}
              <ConfluencePageDetail
                pageId={ev.pageId}
                spaceKey={ev.spaceKey}
                version={typeof ev.version === 'number' ? ev.version : undefined}
                onClose={() => setSelectedItemId(null)}
              />
            </div>
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <div className={`${inline ? 'h-[calc(100vh-4rem)]' : 'fixed inset-0 z-[100]'} flex flex-col ${inline ? '' : (isDark ? 'bg-slate-900/95' : 'bg-white/95')} ${inline ? '' : 'backdrop-blur-sm'} overflow-hidden`}>
      <NotificationPermissionDialog isDark={isDark} />
      {isReview && (
        <div className={`flex-shrink-0 px-4 py-2 text-xs font-medium text-center ${isDark ? 'bg-amber-500/10 text-amber-300 border-b border-amber-500/20' : 'bg-amber-50 text-amber-700 border-b border-amber-200'}`}>
          Reviewing past analysis — data is read-only
        </div>
      )}
      {/* Header - hidden when inline */}
      {!inline && (
      <div className={`flex-shrink-0 z-10 ${isDark ? 'bg-slate-900/80' : 'bg-white/80'} backdrop-blur-md border-b ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={activePreset ? handleBackToPresets : onClose} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}>
              <FontAwesomeIcon icon={faArrowLeft} className={`h-5 w-5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`} />
            </button>
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faLayerGroup} className="h-5 w-5 text-blue-400" />
              <FontAwesomeIcon icon={faCodeCommit} className="h-4 w-4 text-orange-400" />
            </div>
            <div>
              <h1 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {activePreset ? activePreset.name : 'Combined View'}
              </h1>
              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                {activePreset ? 'Preset — Git + Jira' : 'Git Commits + Jira Activity — unified timeline'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {(totalGit > 0 || totalJira > 0) && (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                {totalGit > 0 && <span className="flex items-center gap-1"><FontAwesomeIcon icon={faCodeCommit} className="h-3 w-3 text-orange-400" />{totalGit}</span>}
                {totalJira > 0 && <span className="flex items-center gap-1"><FontAwesomeIcon icon={faLayerGroup} className="h-3 w-3 text-blue-400" />{totalJira}</span>}
                {linkedItems.length > 0 && <span className="flex items-center gap-1"><FontAwesomeIcon icon={faArrowRightArrowLeft} className="h-3 w-3 text-indigo-400" />{linkedItems.length} linked</span>}
                <span>| {timeline.length} in view</span>
              </div>
            )}
            <button onClick={() => setShowSelector(s => !s)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-200 hover:bg-gray-300'}`}>
              <FontAwesomeIcon icon={faLayerGroup} className={`h-3.5 w-3.5 ${isDark ? 'text-white/60' : 'text-gray-600'}`} />
              <span className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{showSelector ? 'Hide' : 'Show'} Selector</span>
            </button>
            {dataReady && (totalGit > 0 || totalJira > 0) && (
              <button
                disabled={pdfDownloading}
                onClick={async () => {
                  setPdfDownloading(true);
                  try {
                    const fromDate = new Date(customFrom);
                    const toDate = new Date(customTo);
                    const repoBreakdown = new Map<string, number>();
                    for (const c of gitCommits) { repoBreakdown.set(c.repoName, (repoBreakdown.get(c.repoName) || 0) + 1); }

                    generateAndDownloadReport('combined', {
                        generatedAt: new Date().toISOString(),
                        dateRange: { start: fromDate.toISOString(), end: toDate.toISOString() },
                        lookbackDays,
                        jiraProjectKey: selectedJiraProject || undefined,
                        repos: availableRepos.filter(r => selectedRepoIds.has(r.id)).map(r => ({ repoId: r.id, repoName: r.name, repoType: r.type })),
                        gitSummary: {
                          totalCommits: gitCommits.length,
                          totalAdditions: gitCommits.reduce((s, c) => s + (c.additions || 0), 0),
                          totalDeletions: gitCommits.reduce((s, c) => s + (c.deletions || 0), 0),
                          repoBreakdown: Array.from(repoBreakdown.entries()).map(([repo, commits]) => ({ repo, commits })).sort((a, b) => b.commits - a.commits),
                        },
                        jiraSummary: {
                          totalEvents: jiraEvents.length,
                          statusBreakdown: statusBreakdown,
                          issueTypeBreakdown: issueTypeBreakdown,
                          sprintSummary: jiraSprintSummary,
                        },
                        userProfiles: userProfiles.map(p => ({ name: p.name, gitCommits: p.gitCommits, jiraEvents: p.jiraEvents, additions: p.additions, deletions: p.deletions })),
                        linkedItems: linkedItems.map(g => ({ issueKey: g.issueKey, issueSummary: g.issueSummary, issueType: g.issueType, latestStatus: g.latestStatus, commitCount: g.commits.length, jiraEventCount: g.jiraEvents.length })),
                        recentTimeline: timeline.slice(0, 50).map(item => ({
                          timestamp: item.timestamp,
                          source: item.source,
                          userName: item.userName,
                          summary: item.gitCommit ? `${item.gitCommit.hash.slice(0, 7)} - ${item.gitCommit.message.split('\n')[0]}` : item.jiraEvent ? `${item.jiraEvent.issueKey} ${item.jiraEvent.issueSummary}` : '',
                        })),
                      });
                  } catch (err) {
                    console.error('Failed to download combined PDF:', err);
                  } finally {
                    setPdfDownloading(false);
                  }
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors ${isDark ? 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300' : 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700'}`}
              >
                {pdfDownloading ? <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" /> : <FontAwesomeIcon icon={faDownload} className="h-3.5 w-3.5" />}
                <span className="text-xs">{pdfDownloading ? 'Generating...' : 'PDF Report'}</span>
              </button>
            )}
            <button onClick={() => setIsDark(d => !d)} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}>
              {isDark ? <FontAwesomeIcon icon={faSun} className="h-4 w-4 text-amber-400" /> : <FontAwesomeIcon icon={faMoon} className="h-4 w-4 text-slate-600" />}
            </button>
            <button onClick={onClose} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}>
              <FontAwesomeIcon icon={faXmark} className={`h-5 w-5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`} />
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Inline toolbar — shown when header is hidden (inline mode) */}
      {inline && (
        <div className={`flex-shrink-0 flex items-center justify-between px-6 py-2 border-b ${isDark ? 'border-slate-700/50' : 'border-slate-200'}`}>
          <div className="flex items-center gap-3">
            {(totalGit > 0 || totalJira > 0) && (
              <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                {totalGit > 0 && <span className="flex items-center gap-1"><FontAwesomeIcon icon={faCodeCommit} className="h-3 w-3 text-orange-400" />{totalGit}</span>}
                {totalJira > 0 && <span className="flex items-center gap-1"><FontAwesomeIcon icon={faLayerGroup} className="h-3 w-3 text-blue-400" />{totalJira}</span>}
                {linkedItems.length > 0 && <span className="flex items-center gap-1"><FontAwesomeIcon icon={faArrowRightArrowLeft} className="h-3 w-3 text-indigo-400" />{linkedItems.length} linked</span>}
                <span>| {timeline.length} in view</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowSelector(s => !s)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-colors ${isDark ? 'bg-white/10 hover:bg-white/20' : 'bg-gray-200 hover:bg-gray-300'}`}>
              <FontAwesomeIcon icon={faLayerGroup} className={`h-3.5 w-3.5 ${isDark ? 'text-white/60' : 'text-gray-600'}`} />
              <span className={`text-xs ${isDark ? 'text-white/60' : 'text-gray-600'}`}>{showSelector ? 'Hide' : 'Show'} Selector</span>
            </button>
            {dataReady && (totalGit > 0 || totalJira > 0) && (
              <button
                disabled={pdfDownloading}
                onClick={async () => {
                  setPdfDownloading(true);
                  try {
                    const fromDate = new Date(customFrom);
                    const toDate = new Date(customTo);
                    const repoBreakdown = new Map<string, number>();
                    for (const c of gitCommits) { repoBreakdown.set(c.repoName, (repoBreakdown.get(c.repoName) || 0) + 1); }
                    generateAndDownloadReport('combined', {
                        generatedAt: new Date().toISOString(),
                        dateRange: { start: fromDate.toISOString(), end: toDate.toISOString() },
                        lookbackDays,
                        jiraProjectKey: selectedJiraProject || undefined,
                        repos: availableRepos.filter(r => selectedRepoIds.has(r.id)).map(r => ({ repoId: r.id, repoName: r.name, repoType: r.type })),
                        gitSummary: {
                          totalCommits: gitCommits.length,
                          totalAdditions: gitCommits.reduce((s, c) => s + (c.additions || 0), 0),
                          totalDeletions: gitCommits.reduce((s, c) => s + (c.deletions || 0), 0),
                          repoBreakdown: Array.from(repoBreakdown.entries()).map(([repo, commits]) => ({ repo, commits })).sort((a, b) => b.commits - a.commits),
                        },
                        jiraSummary: {
                          totalEvents: jiraEvents.length,
                          statusBreakdown: statusBreakdown,
                          issueTypeBreakdown: issueTypeBreakdown,
                          sprintSummary: jiraSprintSummary,
                        },
                        userProfiles: userProfiles.map(p => ({ name: p.name, gitCommits: p.gitCommits, jiraEvents: p.jiraEvents, additions: p.additions, deletions: p.deletions })),
                        linkedItems: linkedItems.map(g => ({ issueKey: g.issueKey, issueSummary: g.issueSummary, issueType: g.issueType, latestStatus: g.latestStatus, commitCount: g.commits.length, jiraEventCount: g.jiraEvents.length })),
                        recentTimeline: timeline.slice(0, 50).map(item => ({
                          timestamp: item.timestamp,
                          source: item.source,
                          userName: item.userName,
                          summary: item.gitCommit ? `${item.gitCommit.hash.slice(0, 7)} - ${item.gitCommit.message.split('\n')[0]}` : item.jiraEvent ? `${item.jiraEvent.issueKey} ${item.jiraEvent.issueSummary}` : '',
                        })),
                      });
                  } catch (err) {
                    console.error('Failed to download combined PDF:', err);
                  } finally {
                    setPdfDownloading(false);
                  }
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-colors ${isDark ? 'bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300' : 'bg-emerald-100 hover:bg-emerald-200 text-emerald-700'}`}
              >
                {pdfDownloading ? <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin" /> : <FontAwesomeIcon icon={faDownload} className="h-3.5 w-3.5" />}
                <span className="text-xs">{pdfDownloading ? 'Generating...' : 'PDF Report'}</span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* Preset tab bar — shown only when viewing a preset */}
      {allPresets.length > 0 && activePreset && (
        <div className={`flex-shrink-0 flex items-center gap-1 px-4 py-1.5 border-b overflow-x-auto ${isDark ? 'border-slate-700/50 bg-slate-900/40' : 'border-slate-200 bg-slate-50/80'}`}>
          <button
            onClick={handleBackToPresets}
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
              !activePreset
                ? (isDark ? 'bg-blue-500/20 text-blue-300 ring-1 ring-blue-500/30' : 'bg-blue-100 text-blue-700 ring-1 ring-blue-300')
                : (isDark ? 'text-slate-500 hover:text-slate-300 hover:bg-slate-800' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100')
            }`}
          >
            <FontAwesomeIcon icon={faBolt} className="h-3 w-3" />
            All Presets
          </button>
          <div className={`w-px h-4 mx-1 ${isDark ? 'bg-slate-700' : 'bg-slate-300'}`} />
          {allPresets.map(preset => {
            const isActive = activePreset?.id === preset.id;
            return (
              <button
                key={preset.id}
                onClick={() => handleSwitchPreset(preset)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                  isActive
                    ? (isDark ? 'bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/30 shadow-sm' : 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-300 shadow-sm')
                    : (isDark ? 'text-slate-500 hover:text-slate-300 hover:bg-slate-800' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100')
                }`}
              >
                {preset.pinned && <FontAwesomeIcon icon={faThumbtack} className="h-2.5 w-2.5" />}
                <span className="truncate max-w-[140px]">{preset.name}</span>
                {isActive && <FontAwesomeIcon icon={faCircleCheck} className="h-2.5 w-2.5 flex-shrink-0" />}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Left: Selector Sidebar — CrossProject style */}
        {showSelector && (
          <div className="w-80 border-r border-gray-200/70 dark:border-white/[0.06] p-4 flex flex-col flex-shrink-0 overflow-hidden bg-white/50 dark:bg-slate-950/40 backdrop-blur-xl">
            <div className="flex items-center justify-between mb-3">
              <h2 className={`text-sm font-semibold flex items-center gap-1.5 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                <FontAwesomeIcon icon={faCodeCommit} className="h-3.5 w-3.5 text-orange-400" /> Repositories
              </h2>
              <div className="flex gap-1">
                <button onClick={() => setSelectedRepoIds(new Set(availableRepos.map(r => r.id)))}
                  className={`text-xs px-2 py-1 rounded ${isDark ? 'bg-white/10 hover:bg-white/20 text-white/60' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}>All</button>
                <button onClick={() => setSelectedRepoIds(new Set())}
                  className={`text-xs px-2 py-1 rounded ${isDark ? 'bg-white/10 hover:bg-white/20 text-white/60' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}>None</button>
              </div>
            </div>
            {availableRepos.length > 8 && (
              <input type="text" value={repoSearch} onChange={(e) => setRepoSearch(e.target.value)} placeholder="Search repos..."
                className={`w-full px-3 py-1.5 text-xs rounded-lg border mb-3 outline-none focus:ring-1 focus:ring-blue-500 ${isDark ? 'bg-white/[0.03] border-white/[0.08] text-white placeholder-white/30' : 'bg-white border-slate-200 text-slate-900 placeholder-slate-400'}`} />
            )}
            <div className="flex-1 overflow-y-auto space-y-1 min-h-0">
              {filteredRepos.map(repo => {
                const isSelected = selectedRepoIds.has(repo.id);
                const color = repoColorMap.get(repo.id);
                return (
                  <button key={repo.id} onClick={() => toggleRepo(repo.id)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-left transition-all ${
                      isSelected
                        ? (isDark ? 'bg-white/[0.06] border border-white/10' : 'bg-blue-50 border border-blue-200')
                        : (isDark ? 'hover:bg-white/[0.03] border border-transparent' : 'hover:bg-gray-50 border border-transparent')
                    }`}>
                    {isSelected ? (
                      <FontAwesomeIcon icon={faSquareCheck} className={`h-4 w-4 flex-shrink-0 ${color?.text || 'text-blue-400'}`} />
                    ) : (
                      <FontAwesomeIcon icon={faSquare} className={`h-4 w-4 flex-shrink-0 ${isDark ? 'text-white/20' : 'text-gray-300'}`} />
                    )}
                    {color && isSelected && <div className={`w-2 h-2 rounded-full flex-shrink-0 ${color.dot}`} />}
                    <span className={`text-xs font-medium truncate flex-1 ${isSelected ? (isDark ? 'text-white' : 'text-slate-900') : (isDark ? 'text-white/60' : 'text-gray-600')}`}>{repo.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full flex-shrink-0 ${isDark ? 'bg-white/5 text-white/30' : 'bg-gray-100 text-gray-400'}`}>{repo.type}</span>
                  </button>
                );
              })}
            </div>

            {/* Jira project */}
            <div className={`mt-3 rounded-lg border p-3 ${isDark ? 'bg-white/[0.02] border-white/[0.05]' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center gap-2 mb-2">
                <FontAwesomeIcon icon={faLayerGroup} className="h-3.5 w-3.5 text-blue-400" />
                <span className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Jira Project</span>
              </div>
              {!hasJira ? (
                <p className={`text-[10px] ${isDark ? 'text-white/40' : 'text-slate-400'}`}>Jira not configured.</p>
              ) : jiraProjectsLoading ? (
                <div className="flex items-center gap-2 text-xs text-slate-500"><FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" /> Loading...</div>
              ) : (
                <select value={selectedJiraProject} onChange={(e) => setSelectedJiraProject(e.target.value)}
                  className={`w-full px-2.5 py-1.5 text-xs rounded-lg border outline-none focus:ring-1 focus:ring-blue-500 ${isDark ? 'bg-white/[0.03] border-white/[0.08] text-white' : 'bg-white border-slate-200 text-slate-900'}`}>
                  <option value="">Select project...</option>
                  {jiraProjects.map(p => <option key={p.key} value={p.key}>{p.name} ({p.key})</option>)}
                </select>
              )}
            </div>

            {/* Confluence space */}
            {hasConfluence && (
              <div className={`mt-2 rounded-lg border p-3 ${isDark ? 'bg-white/[0.02] border-white/[0.05]' : 'bg-gray-50 border-gray-200'}`}>
                <div className="flex items-center gap-2 mb-2">
                  <FontAwesomeIcon icon={faFile} className="h-3.5 w-3.5 text-sky-400" />
                  <span className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Confluence Space</span>
                </div>
                {confluenceSpacesLoading ? (
                  <div className="flex items-center gap-2 text-xs text-slate-500"><FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin" /> Loading...</div>
                ) : (
                  <ConfluenceSpaceCombobox
                    value={selectedConfluenceSpace}
                    onChange={setSelectedConfluenceSpace}
                    spaces={confluenceSpaces}
                    isDark={isDark}
                    showAllOption
                  />
                )}
              </div>
            )}

            {/* Time Range */}
            <div className={`mt-2 rounded-lg border p-3 ${isDark ? 'bg-white/[0.02] border-white/[0.05]' : 'bg-gray-50 border-gray-200'}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <FontAwesomeIcon icon={faCalendarDays} className={`h-3.5 w-3.5 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
                  <span className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Time Range</span>
                </div>
                <button
                  onClick={() => setShowDatePicker(v => !v)}
                  className={`text-[10px] px-2 py-0.5 rounded transition-colors ${
                    showDatePicker
                      ? (isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-cyan-100 text-cyan-700')
                      : (isDark ? 'text-white/40 hover:text-white/60' : 'text-slate-400 hover:text-slate-600')
                  }`}
                >
                  {showDatePicker ? 'Hide' : 'Custom'}
                </button>
              </div>
              <div className="flex flex-wrap gap-1">
                {[
                  {
                    label: 'Friday -> Monday (now)',
                    getValue: () => {
                      const now = new Date();
                      return {
                        from: previousFriday(now),
                        to: now
                      };
                    }
                  },
                  { label: 'Yesterday', days: -1 },
                  { label: '24h', days: 1 },
                  { label: '7d', days: 7 },
                  { label: '2w', days: 14 },
                  { label: '30d', days: 30 },
                  { label: '60d', days: 60 },
                  { label: '90d', days: 90 },
                  { label: '6mo', days: 180 },
                  { label: 'All Time', days: -2 },
                ].map(preset => (
                  <button
                    key={preset.label}
                    onClick={() => {
                      setDateRangePreset(preset.getValue ? 'friday-monday' : (preset.days || 0));
                      let from: Date;
                      let to: Date;

                      if (preset.getValue) {
                        const value = preset.getValue();
                        from = value.from;
                        to = value.to;
                      } else if (preset.days === -1) {
                        from = subDays(new Date(), 1);
                        from.setHours(0, 0, 0, 0);
                        to = subDays(new Date(), 1);
                        to.setHours(23, 59, 59, 999);
                      } else if (preset.days === -2) {
                        from = new Date();
                        from.setFullYear(from.getFullYear() - 10);
                        to = new Date();
                      } else {
                        to = new Date();
                        from = subDays(to, preset.days);
                      }
                      setCustomFrom(toLocalDateStr(from));
                      setCustomTo(toLocalDateStr(to));
                      setShowDatePicker(false);
                    }}
                    className={`px-2.5 py-1 text-[10px] font-medium rounded transition-colors ${
                      (preset.getValue ? dateRangePreset === 'friday-monday' : dateRangePreset === preset.days) && !showDatePicker
                        ? (isDark ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'bg-cyan-100 text-cyan-700 border border-cyan-300')
                        : (isDark ? 'bg-white/5 text-white/50 hover:bg-white/10 border border-transparent' : 'bg-white text-gray-500 hover:bg-gray-100 border border-transparent')
                    }`}>
                    {preset.label}
                  </button>
                ))}
              </div>
              {showDatePicker && (
                <div className="flex gap-2 items-center mt-2">
                  <input
                    type="date"
                    value={customFrom}
                    onChange={(e) => { setCustomFrom(e.target.value); setDateRangePreset(0); }}
                    max={customTo}
                    className={`flex-1 px-2 py-1 text-[10px] rounded border outline-none ${isDark ? 'bg-white/[0.03] border-white/[0.08] text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                  />
                  <span className={`text-[10px] ${isDark ? 'text-white/30' : 'text-slate-400'}`}>to</span>
                  <input
                    type="date"
                    value={customTo}
                    onChange={(e) => { setCustomTo(e.target.value); setDateRangePreset(0); }}
                    min={customFrom}
                    max={toLocalDateStr()}
                    className={`flex-1 px-2 py-1 text-[10px] rounded border outline-none ${isDark ? 'bg-white/[0.03] border-white/[0.08] text-white' : 'bg-white border-slate-200 text-slate-900'}`}
                  />
                </div>
              )}
              {/* Display selected range */}
              <div className={`text-[10px] mt-1.5 ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
                {new Date(customFrom).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                {' — '}
                {new Date(customTo).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                {' '}
                ({lookbackDays} days)
              </div>
            </div>

            {/* Summary + Analyze */}
            <div className={`mt-2 text-[10px] px-1 ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
              {selectedRepoIds.size} repo{selectedRepoIds.size !== 1 ? 's' : ''} selected
              {selectedJiraProject && <> &middot; {selectedJiraProject}</>}
            </div>
            <button onClick={fetchAll} disabled={loading || (selectedRepoIds.size === 0 && !selectedJiraProject)}
              className={`mt-2 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm transition-all ${
                loading || (selectedRepoIds.size === 0 && !selectedJiraProject)
                  ? (isDark ? 'bg-white/5 text-white/20 cursor-not-allowed' : 'bg-gray-100 text-gray-400 cursor-not-allowed')
                  : 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:from-blue-600 hover:to-cyan-600 shadow-lg shadow-blue-500/25'
              }`}>
              {loading ? <><FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin" /> Analyzing...</> : <><FontAwesomeIcon icon={faRotate} className="h-4 w-4" /> Analyze &amp; Combine</>}
            </button>
            {loading && (gitLoading || jiraLoading || confluenceLoading || notionLoading || googleDocsLoading) && (
              <div className="mt-3 space-y-2">
                {gitLoading && (
                  <>
                    <div className="flex items-center justify-between text-[10px]">
                      <span className={isDark ? 'text-white/50' : 'text-slate-500'}>Git: {gitProgressIndex}/{gitProgressTotal} repos</span>
                      <span className={isDark ? 'text-cyan-400' : 'text-cyan-600'}>{gitProgressTotal > 0 ? Math.round((gitProgressIndex / gitProgressTotal) * 100) : 0}%</span>
                    </div>
                    <div className={`w-full h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-white/10' : 'bg-slate-200'}`}>
                      <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-500" style={{ width: `${gitProgressTotal > 0 ? (gitProgressIndex / gitProgressTotal) * 100 : 0}%` }} />
                    </div>
                    <p className={`text-[10px] truncate ${isDark ? 'text-white/40' : 'text-slate-400'}`}>{gitProgressMessage}</p>
                  </>
                )}
                {jiraLoading && (
                  <div className="flex items-center gap-2 text-[10px]">
                    <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin text-blue-400" />
                    <span className={isDark ? 'text-white/40' : 'text-slate-400'}>Loading Jira data...</span>
                  </div>
                )}
                {confluenceLoading && (
                  <div className="flex items-center gap-2 text-[10px]">
                    <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin text-sky-400" />
                    <span className={isDark ? 'text-white/40' : 'text-slate-400'}>Loading Confluence data...</span>
                  </div>
                )}
                {notionLoading && (
                  <div className="flex items-center gap-2 text-[10px]">
                    <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin text-purple-400" />
                    <span className={isDark ? 'text-white/40' : 'text-slate-400'}>Loading Notion data...</span>
                  </div>
                )}
                {googleDocsLoading && (
                  <div className="flex items-center gap-2 text-[10px]">
                    <FontAwesomeIcon icon={faSpinner} className="h-3 w-3 animate-spin text-blue-400" />
                    <span className={isDark ? 'text-white/40' : 'text-slate-400'}>Loading Google Docs data...</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Right: Main content area */}
        <div className="flex-1 overflow-y-auto min-h-0 px-6 py-4 space-y-4">
          {error && (
            <div className="flex items-center gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
              <FontAwesomeIcon icon={faCircleExclamation} className="h-5 w-5 text-red-400 flex-shrink-0" />
              <p className="text-sm text-red-300 whitespace-pre-line">{error}</p>
            </div>
          )}

          {/* Preset grid — shown when no data and no active preset */}
          {!dataReady && !loading && !error && !activePreset && (
            <PresetGrid
              availableRepos={availableRepos}
              jiraProjects={jiraProjects}
              hasJira={hasJira}
              confluenceSpaces={confluenceSpaces}
              hasConfluence={hasConfluence}
              isDark={isDark}
              onOpenPreset={handleOpenPreset}
              onAnalyzePreset={handleAnalyzePreset}
              analysisJobs={analysisJobs}
              onPresetsChanged={refreshPresets}
            />
          )}

          {/* Empty state — active preset but no data yet */}
          {!dataReady && !loading && !error && activePreset && (
            <div className={`flex-1 flex items-center justify-center ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
              <div className="text-center">
                <FontAwesomeIcon icon={faLayerGroup} className="h-16 w-16 mx-auto mb-4 opacity-30" />
                <p className={`text-lg font-medium mb-2 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>
                  {activePreset.name}
                </p>
                <p className="text-sm mb-4">No cached snapshot available. Run analysis or select repos in the sidebar.</p>
                <button onClick={handleBackToPresets}
                  className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${isDark ? 'bg-slate-800 hover:bg-slate-700 text-slate-400' : 'bg-slate-100 hover:bg-slate-200 text-slate-500'}`}>
                  ← Back to Presets
                </button>
              </div>
            </div>
          )}

          {/* Main content after fetching — only show when both sources are done */}
          {dataReady && (totalGit > 0 || totalJira > 0) && (
            <div className="space-y-4">
            {/* Tab bar */}
            <div className={`flex items-center gap-1 p-1 rounded-lg ${isDark ? 'bg-slate-800/50' : 'bg-slate-100'}`}>
              <button onClick={() => setActiveTab('timeline')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-xs font-medium transition-colors ${
                  activeTab === 'timeline'
                    ? (isDark ? 'bg-slate-700 text-white shadow' : 'bg-white text-slate-900 shadow')
                    : (isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900')
                }`}>
                <FontAwesomeIcon icon={faCodeCommit} className="h-3.5 w-3.5" /> Timeline
              </button>
              <button onClick={() => setActiveTab('charts')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-xs font-medium transition-colors ${
                  activeTab === 'charts'
                    ? (isDark ? 'bg-slate-700 text-white shadow' : 'bg-white text-slate-900 shadow')
                    : (isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900')
                }`}>
                <FontAwesomeIcon icon={faArrowTrendUp} className="h-3.5 w-3.5" /> Charts
              </button>
              <button onClick={() => setActiveTab('indicators')}
                className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-xs font-medium transition-colors ${
                  activeTab === 'indicators'
                    ? (isDark ? 'bg-slate-700 text-white shadow' : 'bg-white text-slate-900 shadow')
                    : (isDark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900')
                }`}>
                <FontAwesomeIcon icon={faChartBar} className="h-3.5 w-3.5" /> Health Indicators
              </button>
            </div>

            {/* Charts view */}
            {activeTab === 'charts' && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                {/* Daily Activity: Git vs Jira */}
                <div className={`rounded-xl border p-4 ${isDark ? 'bg-white/[0.02] border-white/[0.05]' : 'bg-white border-gray-200 shadow-sm'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faArrowTrendUp} className={`h-3.5 w-3.5 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} />
                    <h3 className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Daily Activity (Git vs Jira)</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={dailyActivityData}>
                      <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                      <XAxis dataKey="date" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                      <YAxis tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                      <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '8px', fontSize: '12px', color: isDark ? '#fff' : '#111' }} />
                      <Legend wrapperStyle={{ fontSize: '11px' }} />
                      <Area type="monotone" dataKey="git" name="Git Commits" stroke="#f97316" fill="#f97316" fillOpacity={0.3} stackId="1" />
                      <Area type="monotone" dataKey="jira" name="Jira Events" stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.3} stackId="1" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>

                {/* Activity by User */}
                <div className={`rounded-xl border p-4 ${isDark ? 'bg-white/[0.02] border-white/[0.05]' : 'bg-white border-gray-200 shadow-sm'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faUsers} className={`h-3.5 w-3.5 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
                    <h3 className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Team Activity (Git + Jira)</h3>
                  </div>
                  <div className="overflow-y-auto max-h-[200px]">
                    <div style={{ height: Math.max(200, userActivityData.length * 32) }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={userActivityData} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                          <XAxis type="number" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                          <YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} width={80} />
                          <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '8px', fontSize: '12px', color: isDark ? '#fff' : '#111' }}
                            labelFormatter={(label: string) => { const item = userActivityData.find(d => d.name === label); return item?.fullName || label; }} />
                          <Legend wrapperStyle={{ fontSize: '11px' }} />
                          <Bar dataKey="gitCommits" name="Git Commits" fill="#f97316" radius={[0, 4, 4, 0]} />
                          <Bar dataKey="jiraEvents" name="Jira Events" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>

                {/* Source Split */}
                <div className={`rounded-xl border p-4 ${isDark ? 'bg-white/[0.02] border-white/[0.05]' : 'bg-white border-gray-200 shadow-sm'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <FontAwesomeIcon icon={faChartPie} className={`h-3.5 w-3.5 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                    <h3 className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Source Distribution</h3>
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={sourceShareData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} style={{ fontSize: '10px' }}>
                        {sourceShareData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '8px', fontSize: '12px', color: isDark ? '#fff' : '#111' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Jira Status Breakdown */}
                {statusChartData.length > 0 && (
                  <div className={`rounded-xl border p-4 ${isDark ? 'bg-white/[0.02] border-white/[0.05]' : 'bg-white border-gray-200 shadow-sm'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <FontAwesomeIcon icon={faFlag} className={`h-3.5 w-3.5 ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`} />
                      <h3 className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Jira Status Transitions</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={statusChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                        <XAxis dataKey="name" tick={{ fontSize: 9, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} interval={0} angle={-30} textAnchor="end" height={50} />
                        <YAxis tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                        <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '8px', fontSize: '12px', color: isDark ? '#fff' : '#111' }} />
                        <Bar dataKey="count" name="Transitions" radius={[4, 4, 0, 0]}>
                          {statusChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Issue Type Distribution */}
                {issueTypeChartData.length > 0 && (
                  <div className={`rounded-xl border p-4 ${isDark ? 'bg-white/[0.02] border-white/[0.05]' : 'bg-white border-gray-200 shadow-sm'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <FontAwesomeIcon icon={faBolt} className={`h-3.5 w-3.5 ${isDark ? 'text-rose-400' : 'text-rose-600'}`} />
                      <h3 className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Issue Type Distribution</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={issueTypeChartData} dataKey="count" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} style={{ fontSize: '10px' }}>
                          {issueTypeChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '8px', fontSize: '12px', color: isDark ? '#fff' : '#111' }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Commits per Repo */}
                {repoChartData.length > 0 && (
                  <div className={`rounded-xl border p-4 ${isDark ? 'bg-white/[0.02] border-white/[0.05]' : 'bg-white border-gray-200 shadow-sm'}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <FontAwesomeIcon icon={faCodeBranch} className={`h-3.5 w-3.5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                      <h3 className={`text-xs font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Commits per Repository</h3>
                    </div>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={repoChartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.1)'} />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                        <YAxis tick={{ fontSize: 10, fill: isDark ? 'rgba(255,255,255,0.4)' : '#6b7280' }} />
                        <Tooltip contentStyle={{ backgroundColor: isDark ? '#1e293b' : '#fff', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#e5e7eb'}`, borderRadius: '8px', fontSize: '12px', color: isDark ? '#fff' : '#111' }}
                          labelFormatter={(label: string) => { const item = repoChartData.find(d => d.name === label); return item?.fullName || label; }} />
                        <Bar dataKey="commits" name="Commits" radius={[4, 4, 0, 0]}>
                          {repoChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}

            {/* Indicators view */}
            {activeTab === 'indicators' && (
              <CombinedIndicators
                gitCommits={gitCommits}
                jiraEvents={jiraEvents}
                sprintSummary={jiraSprintSummary}
                linkedItems={linkedItems}
                lookbackDays={lookbackDays}
                startDate={customFrom}
                endDate={customTo}
                isDark={isDark}
                jiraDomain={jiraDomain || undefined}
                jiraEmail={jiraEmail || undefined}
                jiraApiToken={jiraApiToken || undefined}
                selectedJiraProject={selectedJiraProject || undefined}
              />
            )}

            {/* Waterfall Timeline — always visible when there are events */}
            {combinedWaterfallEvents.length > 0 && (
              <WaterfallView
                events={combinedWaterfallEvents}
                title="Combined Waterfall Timeline"
                onEventClick={(evt) => {
                  // Switch to timeline tab if not already there
                  setActiveTab('timeline');
                  // Expand the date group containing this event
                  const date = toLocalDateStr(new Date(evt.timestamp));
                  setExpandedDates(prev => {
                    const next = new Set(prev);
                    next.add(date);
                    return next;
                  });
                  // Expand the matching ticket group
                  setExpandedTickets(prev => {
                    const next = new Set(prev);
                    next.add(evt.issueKey.toUpperCase());
                    next.add(evt.issueKey);
                    return next;
                  });
                  // Scroll to the ticket group after DOM updates
                  setTimeout(() => {
                    const el = document.getElementById(`combined-ticket-${evt.issueKey}`) ||
                               document.getElementById(`combined-ticket-${evt.issueKey.toUpperCase()}`);
                    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }, 200);
                }}
              />
            )}

            {/* Timeline view */}
            {activeTab === 'timeline' && (
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Left filter sidebar */}
              <div className="space-y-3">

              {/* Search */}
              <div className={`${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-slate-50 border-slate-200'} border rounded-xl p-3`}>
                <div className="relative">
                  <FontAwesomeIcon icon={faMagnifyingGlass} className={`absolute left-2.5 top-2.5 h-3.5 w-3.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`} />
                  <input type="text" value={filterSearch} onChange={(e) => setFilterSearch(e.target.value)} placeholder="Search commits & issues..."
                    className={`w-full pl-8 pr-3 py-2 rounded-lg text-xs border focus:outline-none focus:ring-2 focus:ring-blue-500/30 ${isDark ? 'bg-slate-700/30 border-slate-600 text-white placeholder-slate-500' : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400'}`} />
                </div>
              </div>

              {/* Combined-specific: Linked / Unlinked toggles */}
              {linkedItems.length > 0 && (
                <div className="flex gap-1.5">
                  <button onClick={() => { setShowLinkedOnly(v => !v); setShowUnlinkedOnly(false); }}
                    className={`flex-1 flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-colors border ${showLinkedOnly ? (isDark ? 'bg-indigo-500/20 border-indigo-500/30 text-indigo-300' : 'bg-indigo-100 border-indigo-200 text-indigo-700') : (isDark ? 'bg-slate-800/50 border-slate-700/50 text-slate-400 hover:text-white' : 'bg-slate-50 border-slate-200 text-slate-600')}`}>
                    <span className="flex items-center gap-1.5"><FontAwesomeIcon icon={faArrowRightArrowLeft} className="h-3.5 w-3.5" /> Linked</span>
                    <span className="text-[10px]">{linkedItems.length}</span>
                  </button>
                  <button onClick={() => { setShowUnlinkedOnly(v => !v); setShowLinkedOnly(false); }}
                    className={`flex-1 flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-colors border ${showUnlinkedOnly ? (isDark ? 'bg-orange-500/20 border-orange-500/30 text-orange-300' : 'bg-orange-100 border-orange-200 text-orange-700') : (isDark ? 'bg-slate-800/50 border-slate-700/50 text-slate-400 hover:text-white' : 'bg-slate-50 border-slate-200 text-slate-600')}`}>
                    <span className="flex items-center gap-1.5"><FontAwesomeIcon icon={faCodeCommit} className="h-3.5 w-3.5" /> Unlinked</span>
                  </button>
                </div>
              )}

              {/* Team filter */}
              <div className={`${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-slate-50 border-slate-200'} border rounded-xl p-3 space-y-1`}>
                <h3 className={`text-[10px] font-medium uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  <FontAwesomeIcon icon={faUsers} className="h-3 w-3 inline mr-1" /> Team ({userProfiles.length})
                </h3>
                <button onClick={() => setFilterUser('')}
                  className={`w-full text-left px-2 py-1 rounded text-xs transition-colors ${!filterUser ? 'bg-blue-500/20 text-blue-300' : (isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700/50' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100')}`}>
                  All users
                </button>
                {userProfiles.map(profile => (
                  <button key={profile.name} onClick={() => setFilterUser(profile.name)}
                    className={`w-full text-left px-2 py-1 rounded text-xs transition-colors ${filterUser === profile.name ? 'bg-blue-500/20 text-blue-300' : (isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700/50' : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100')}`}>
                    <div className="flex items-center justify-between">
                      <span className="truncate font-medium">{profile.name}</span>
                      <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                        {profile.gitCommits > 0 && <span className="text-[9px] text-orange-400">{profile.gitCommits}c</span>}
                        {profile.jiraEvents > 0 && <span className="text-[9px] text-blue-400">{profile.jiraEvents}j</span>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>

              {/* Source filter */}
              <div className={`${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-slate-50 border-slate-200'} border rounded-xl p-3 space-y-1`}>
                <h3 className={`text-[10px] font-medium uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                  <FontAwesomeIcon icon={faFilter} className="h-3 w-3 inline mr-1" /> Source
                </h3>
                {(['all', 'git', 'jira', 
                  ...(confluenceEvents.length > 0 ? ['confluence' as const] : []),
                  ...(notionEvents.length > 0 ? ['notion' as const] : []),
                  ...(googleDocEvents.length > 0 ? ['google-docs' as const] : []),
                ] as const).map(src => (
                  <button key={src} onClick={() => setFilterSource(src)}
                    className={`w-full text-left px-2 py-1 rounded text-xs transition-colors capitalize ${filterSource === src ? 'bg-blue-500/20 text-blue-300' : (isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700/50' : 'text-slate-600 hover:bg-slate-100')}`}>
                    {src === 'all' ? 'All sources' : 
                     src === 'git' ? '🔸 Git commits' : 
                     src === 'jira' ? '🔹 Jira events' : 
                     src === 'confluence' ? '🔷 Confluence pages' :
                     src === 'notion' ? <><FontAwesomeIcon icon={faNotion} className="h-3 w-3 mr-1" /> Notion pages</> :
                     <><FontAwesomeIcon icon={faGoogle} className="h-3 w-3 mr-1" /> Google Docs</>}
                  </button>
                ))}
              </div>

              {/* Confluence Event Types */}
              {confluenceEventTypeBreakdown.length > 0 && (
                <div className={`${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-slate-50 border-slate-200'} border rounded-xl p-3 space-y-1`}>
                  <h3 className={`text-[10px] font-medium uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    <FontAwesomeIcon icon={faComment} className="h-3 w-3 inline mr-1" /> Confluence Events
                  </h3>
                  {filterConfluenceEventTypes.size > 0 && (
                    <button onClick={() => setFilterConfluenceEventTypes(new Set())} className="w-full text-left px-2 py-0.5 rounded text-[10px] text-sky-400 hover:text-sky-300 transition-colors">✕ Clear event filter</button>
                  )}
                  {confluenceEventTypeBreakdown.map(({ key, count }) => {
                    const meta = CONFLUENCE_EVENT_LABELS[key];
                    const isActive = filterConfluenceEventTypes.has(key);
                    const colorMap: Record<string, string> = {
                      emerald: isDark ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-emerald-50 text-emerald-700 border-emerald-200',
                      sky: isDark ? 'bg-sky-500/10 text-sky-400 border-sky-500/20' : 'bg-sky-50 text-sky-700 border-sky-200',
                      slate: isDark ? 'bg-slate-500/10 text-slate-400 border-slate-500/20' : 'bg-slate-100 text-slate-500 border-slate-200',
                      purple: isDark ? 'bg-purple-500/10 text-purple-400 border-purple-500/20' : 'bg-purple-50 text-purple-700 border-purple-200',
                      violet: isDark ? 'bg-violet-500/10 text-violet-400 border-violet-500/20' : 'bg-violet-50 text-violet-700 border-violet-200',
                    };
                    return (
                      <button key={key}
                        onClick={() => setFilterConfluenceEventTypes(prev => {
                          const next = new Set(prev);
                          if (next.has(key)) next.delete(key); else next.add(key);
                          return next;
                        })}
                        className={`w-full flex items-center justify-between text-xs cursor-pointer rounded px-2 py-1 transition-colors border ${
                          isActive ? 'ring-1 ring-sky-500/40 ' + (colorMap[meta?.color ?? 'sky']) : (isDark ? 'border-transparent text-slate-300 hover:bg-slate-700/30 hover:text-white' : 'border-transparent text-slate-700 hover:bg-slate-100')
                        }`}>
                        <span className={`flex items-center gap-1.5`}>
                          {isActive && <span className="text-sky-400">✓</span>}
                          <span className={`px-1.5 py-0.5 rounded border text-[10px] ${isActive ? colorMap[meta?.color ?? 'sky'] : (isDark ? 'bg-slate-700/40 border-slate-600 text-slate-400' : 'bg-slate-100 border-slate-200 text-slate-500')}`}>
                            {meta?.label ?? key}
                          </span>
                        </span>
                        <span className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{count}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Confluence Spaces (only when multiple spaces present) */}
              {confluenceSpaceBreakdown.length > 1 && (
                <div className={`${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-slate-50 border-slate-200'} border rounded-xl p-3 space-y-1`}>
                  <h3 className={`text-[10px] font-medium uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    Confluence Spaces
                  </h3>
                  {filterConfluenceSpace && (
                    <button onClick={() => setFilterConfluenceSpace('')} className="w-full text-left px-2 py-0.5 rounded text-[10px] text-sky-400 hover:text-sky-300 transition-colors">✕ Clear space filter</button>
                  )}
                  {confluenceSpaceBreakdown.map(s => (
                    <button key={s.key} onClick={() => setFilterConfluenceSpace(filterConfluenceSpace === s.key ? '' : s.key)}
                      className={`w-full flex items-center justify-between text-xs cursor-pointer rounded px-2 py-1 transition-colors ${filterConfluenceSpace === s.key ? 'bg-sky-500/20 text-sky-300 ring-1 ring-sky-500/40' : (isDark ? 'text-slate-300 hover:bg-slate-700/30 hover:text-white' : 'text-slate-700 hover:bg-slate-100')}`}>
                      <span className="truncate">{s.name}</span>
                      <span className={`text-[10px] flex-shrink-0 ml-1 font-mono ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{s.count}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Status Breakdown (Jira) */}
              {statusBreakdown.length > 0 && (
                <div className={`${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-slate-50 border-slate-200'} border rounded-xl p-3 space-y-1`}>
                  <h3 className={`text-[10px] font-medium uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Status Breakdown</h3>
                  {filterStatus && (
                    <button onClick={() => setFilterStatus('')} className="w-full text-left px-2 py-0.5 rounded text-[10px] text-blue-400 hover:text-blue-300 transition-colors">✕ Clear status filter</button>
                  )}
                  {statusBreakdown.map(s => (
                    <button key={s.status} onClick={() => setFilterStatus(filterStatus === s.status ? '' : s.status)}
                      className={`w-full flex items-center justify-between text-xs cursor-pointer rounded px-1 py-0.5 transition-colors ${filterStatus === s.status ? 'bg-blue-500/20 ring-1 ring-blue-500/40' : (isDark ? 'hover:bg-slate-700/30' : 'hover:bg-slate-100')}`}>
                      <span className={`px-2 py-0.5 text-[10px] rounded border ${getStatusColor(s.status)}`}>{s.status}</span>
                      <span className="text-slate-500 text-[10px]">{s.count}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Issue Types (Jira) */}
              {issueTypeBreakdown.length > 0 && (
                <div className={`${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-slate-50 border-slate-200'} border rounded-xl p-3 space-y-1`}>
                  <h3 className={`text-[10px] font-medium uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Issue Types</h3>
                  {filterIssueType && (
                    <button onClick={() => setFilterIssueType('')} className="w-full text-left px-2 py-0.5 rounded text-[10px] text-blue-400 hover:text-blue-300 transition-colors">✕ Clear type filter</button>
                  )}
                  {issueTypeBreakdown.map(t => (
                    <button key={t.type} onClick={() => setFilterIssueType(filterIssueType === t.type ? '' : t.type)}
                      className={`w-full flex items-center justify-between text-xs cursor-pointer rounded px-2 py-1 transition-colors ${filterIssueType === t.type ? 'bg-blue-500/20 text-blue-300 ring-1 ring-blue-500/40' : (isDark ? 'text-slate-300 hover:bg-slate-700/30 hover:text-white' : 'text-slate-700 hover:bg-slate-100')}`}>
                      <span>{t.type}</span>
                      <span className="text-slate-500 text-[10px]">{t.count}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Labels Breakdown (Jira) */}
              {labelBreakdown.length > 0 && (
                <div className={`${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-slate-50 border-slate-200'} border rounded-xl p-3 space-y-1`}>
                  <h3 className={`text-[10px] font-medium uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Labels</h3>
                  {filterLabel && (
                    <button onClick={() => setFilterLabel('')} className="w-full text-left px-2 py-0.5 rounded text-[10px] text-blue-400 hover:text-blue-300 transition-colors">✕ Clear label filter</button>
                  )}
                  {(showAllLabels ? labelBreakdown : labelBreakdown.slice(0, 6)).map(l => (
                    <button key={l.label} onClick={() => setFilterLabel(filterLabel === l.label ? '' : l.label)}
                      className={`w-full flex items-center justify-between text-xs cursor-pointer rounded px-2 py-1 transition-colors ${filterLabel === l.label ? 'bg-blue-500/20 text-blue-300 ring-1 ring-blue-500/40' : (isDark ? 'text-slate-300 hover:bg-slate-700/30 hover:text-white' : 'text-slate-700 hover:bg-slate-100')}`}>
                      <span className="truncate">{l.label}</span>
                      <span className="text-slate-500 text-[10px] flex-shrink-0 ml-1">{l.count}</span>
                    </button>
                  ))}
                  {labelBreakdown.length > 6 && (
                    <button onClick={() => setShowAllLabels(!showAllLabels)}
                      className={`w-full text-center text-[10px] font-medium px-3 py-1 rounded-md transition-colors ${isDark ? 'bg-slate-700/60 text-slate-400 hover:bg-slate-600/70' : 'bg-gray-200/60 text-gray-600 hover:bg-gray-300/70'}`}>
                      {showAllLabels ? 'Show less' : `Show all ${labelBreakdown.length} labels`}
                    </button>
                  )}
                </div>
              )}

              {/* Sprints (Jira) — same style as JiraActivityView */}
              {sprintBreakdown.length > 0 && (
                <div className={`${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-slate-50 border-slate-200'} border rounded-xl p-3 space-y-1.5`}>
                  <h3 className={`text-[10px] font-medium uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Sprints</h3>
                  {filterSprint && (
                    <button onClick={() => setFilterSprint('')} className="w-full text-left px-2 py-0.5 rounded text-[10px] text-blue-400 hover:text-blue-300 transition-colors">✕ Clear sprint filter</button>
                  )}
                  {sprintBreakdown.map((sprint: any) => (
                    <button key={sprint.name} onClick={() => setFilterSprint(filterSprint === sprint.name ? '' : sprint.name)}
                      className={`w-full text-left rounded-lg p-2 cursor-pointer transition-colors ${
                        filterSprint === sprint.name
                          ? (isDark ? 'ring-1 ring-blue-500/40 bg-blue-500/10' : 'ring-1 ring-blue-300 bg-blue-50')
                          : (isDark ? 'bg-slate-900/50 hover:bg-slate-800/70' : 'bg-white hover:bg-slate-50')
                      }`}>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{sprint.name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded flex-shrink-0 ml-1 ${
                          sprint.state === 'active' ? 'bg-emerald-500/20 text-emerald-300'
                            : sprint.state === 'future' ? 'bg-blue-500/20 text-blue-300'
                            : (isDark ? 'bg-slate-600/30 text-slate-400' : 'bg-slate-200 text-slate-500')
                        }`}>{sprint.state}</span>
                      </div>
                      <div className={`text-[10px] mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        {sprint.completedIssues}/{sprint.totalIssues} completed
                      </div>
                    </button>
                  ))}
                </div>
              )}

              {/* Repository filter (Git) */}
              {repoBreakdown.length > 1 && (
                <div className={`${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-slate-50 border-slate-200'} border rounded-xl p-3 space-y-1`}>
                  <h3 className={`text-[10px] font-medium uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    <FontAwesomeIcon icon={faCodeBranch} className="h-3 w-3 inline mr-1" /> Repositories
                  </h3>
                  {filterRepo && (
                    <button onClick={() => setFilterRepo('')} className="w-full text-left px-2 py-0.5 rounded text-[10px] text-blue-400 hover:text-blue-300 transition-colors">✕ Clear repo filter</button>
                  )}
                  {repoBreakdown.map(r => (
                    <button key={r.repo} onClick={() => setFilterRepo(filterRepo === r.repo ? '' : r.repo)}
                      className={`w-full flex items-center justify-between text-xs cursor-pointer rounded px-2 py-1 transition-colors ${filterRepo === r.repo ? 'bg-orange-500/20 text-orange-300 ring-1 ring-orange-500/40' : (isDark ? 'text-slate-300 hover:bg-slate-700/30 hover:text-white' : 'text-slate-700 hover:bg-slate-100')}`}>
                      <span className="truncate">{r.repo}</span>
                      <span className="text-slate-500 text-[10px]">{r.count}</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Branch filter (Git) */}
              {branchBreakdown.length > 1 && (
                <div className={`${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-slate-50 border-slate-200'} border rounded-xl p-3 space-y-1`}>
                  <h3 className={`text-[10px] font-medium uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                    <FontAwesomeIcon icon={faCodeBranch} className="h-3 w-3 inline mr-1" /> Branches
                  </h3>
                  {filterBranch && (
                    <button onClick={() => setFilterBranch('')} className="w-full text-left px-2 py-0.5 rounded text-[10px] text-blue-400 hover:text-blue-300 transition-colors">✕ Clear branch filter</button>
                  )}
                  <div className="max-h-32 overflow-y-auto space-y-0.5">
                    {branchBreakdown.map(b => (
                      <button key={b.branch} onClick={() => setFilterBranch(filterBranch === b.branch ? '' : b.branch)}
                        className={`w-full flex items-center justify-between text-xs cursor-pointer rounded px-2 py-1 transition-colors ${filterBranch === b.branch ? 'bg-orange-500/20 text-orange-300 ring-1 ring-orange-500/40' : (isDark ? 'text-slate-300 hover:bg-slate-700/30 hover:text-white' : 'text-slate-700 hover:bg-slate-100')}`}>
                        <span className="truncate font-mono text-[10px]">{b.branch}</span>
                        <span className="text-slate-500 text-[10px] flex-shrink-0 ml-1">{b.count}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Group by ticket toggle */}
              <button onClick={() => { setGroupByTicket(v => !v); setExpandedTickets(new Set()); }}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-medium transition-colors border ${groupByTicket ? (isDark ? 'bg-cyan-500/20 border-cyan-500/30 text-cyan-300' : 'bg-cyan-100 border-cyan-200 text-cyan-700') : (isDark ? 'bg-slate-800/50 border-slate-700/50 text-slate-400 hover:text-white' : 'bg-slate-50 border-slate-200 text-slate-600')}`}>
                <span className="flex items-center gap-1.5"><FontAwesomeIcon icon={faLayerGroup} className="h-3.5 w-3.5" /> Group by Ticket</span>
                <span className="text-[10px]">{groupByTicket ? 'ON' : 'OFF'}</span>
              </button>

              {/* Show/Hide toggles */}
              <div className={`${isDark ? 'bg-slate-800/50 border-slate-700/50' : 'bg-slate-50 border-slate-200'} border rounded-xl p-3 space-y-1`}>
                <h3 className={`text-[10px] font-medium uppercase tracking-wider mb-1.5 ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>Show/Hide</h3>
                {[
                  { label: 'Git Commits', state: showGitCommits, set: setShowGitCommits },
                  { label: 'Status Changes', state: showJiraStatus, set: setShowJiraStatus },
                  { label: 'Assignments', state: showJiraAssignments, set: setShowJiraAssignments },
                  { label: 'Sprint Changes', state: showJiraSprints, set: setShowJiraSprints },
                  { label: 'Issue Created', state: showJiraCreated, set: setShowJiraCreated },
                  { label: 'Comments', state: showJiraComments, set: setShowJiraComments },
                  { label: 'Hide Merges', state: hideMerges, set: setHideMerges },
                ].map(({ label, state, set }) => (
                  <button key={label} onClick={() => set(!state)}
                    className={`w-full flex items-center justify-between px-2 py-1 text-xs rounded transition-colors ${isDark ? 'hover:bg-slate-700/50' : 'hover:bg-slate-100'}`}>
                    <span className={state ? (isDark ? 'text-white' : 'text-slate-900') : (isDark ? 'text-slate-500 line-through' : 'text-slate-400 line-through')}>{label}</span>
                    {state ? <FontAwesomeIcon icon={faEye} className="h-3 w-3 text-blue-400" /> : <FontAwesomeIcon icon={faEyeSlash} className="h-3 w-3 text-slate-600" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Right: Timeline */}
            <div className="lg:col-span-3 space-y-2">

              {/* Linked Items — Git commits matched to Jira tasks */}
              {linkedItems.length > 0 && (
                <div className={`${isDark ? 'bg-gradient-to-r from-indigo-500/5 to-purple-500/5 border-indigo-500/20' : 'bg-indigo-50 border-indigo-200'} border rounded-xl overflow-hidden`}>
                  <button onClick={() => setShowLinkedSection(v => !v)} className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-indigo-50/50'}`}>
                    <div className="flex items-center gap-2">
                      {showLinkedSection ? <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 text-indigo-400" /> : <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4 text-indigo-400" />}
                      <FontAwesomeIcon icon={faArrowRightArrowLeft} className="h-4 w-4 text-indigo-400" />
                      <span className={`text-sm font-semibold ${isDark ? 'text-indigo-300' : 'text-indigo-700'}`}>
                        Linked Items
                      </span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${isDark ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30' : 'bg-indigo-100 text-indigo-600'}`}>
                        {filteredLinkedItems.length} match{filteredLinkedItems.length !== 1 ? 'es' : ''}
                        {filterUser && filteredLinkedItems.length !== linkedItems.length && (
                          <span className="ml-1 opacity-60">of {linkedItems.length}</span>
                        )}
                      </span>
                    </div>
                    <span className={`text-[10px] ${isDark ? 'text-indigo-400/60' : 'text-indigo-500'}`}>
                      Commits &amp; branches matched to Jira tasks
                    </span>
                  </button>

                  {showLinkedSection && (
                  <div className={`border-t ${isDark ? 'border-indigo-500/10' : 'border-indigo-200/50'}`}>
                    {filteredLinkedItems.map(group => {
                      const isExpanded = expandedLinkedKeys.has(group.issueKey);
                      const totalAdditions = group.commits.reduce((s, c) => s + c.additions, 0);
                      const totalDeletions = group.commits.reduce((s, c) => s + c.deletions, 0);
                      const uniqueAuthors = [...new Set(group.commits.map(c => c.author))];

                      return (
                        <div key={group.issueKey} id={`combined-ticket-${group.issueKey}`} className={`border-b last:border-b-0 ${isDark ? 'border-indigo-500/10' : 'border-indigo-100'}`}>
                          <button
                            onClick={() => toggleLinkedKey(group.issueKey)}
                            className={`w-full px-4 py-3 flex items-start gap-3 text-left transition-colors ${isDark ? 'hover:bg-white/[0.02]' : 'hover:bg-indigo-50/50'} ${isExpanded ? (isDark ? 'bg-white/[0.02]' : 'bg-indigo-50/30') : ''}`}>
                            <div className="mt-0.5 flex-shrink-0">
                              {isExpanded ? <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 text-indigo-400" /> : <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4 text-indigo-400" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <a href={jiraDomain ? `https://${jiraDomain}.atlassian.net/browse/${group.issueKey}` : '#'}
                                  target="_blank" rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-xs font-mono text-indigo-400 hover:text-indigo-300 font-bold">
                                  {group.issueKey}
                                </a>
                                {group.issueType && (
                                  <span className="text-[10px] px-1.5 py-0.5 bg-indigo-500/10 text-indigo-300 border border-indigo-500/20 rounded">{group.issueType}</span>
                                )}
                                {group.latestStatus && (
                                  <span className={`px-2 py-0.5 text-[10px] rounded border ${getStatusColor(group.latestStatus)}`}>{group.latestStatus}</span>
                                )}
                              </div>
                              {group.issueSummary && (
                                <p className={`text-xs mt-1 truncate ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{group.issueSummary}</p>
                              )}
                              <div className="flex items-center gap-3 mt-1.5 text-[10px] text-slate-500 flex-wrap">
                                <span className="flex items-center gap-1">
                                  <FontAwesomeIcon icon={faCodeCommit} className="h-3 w-3 text-orange-400" />
                                  {group.commits.length} commit{group.commits.length !== 1 ? 's' : ''}
                                </span>
                                <span className="flex items-center gap-1">
                                  <FontAwesomeIcon icon={faLayerGroup} className="h-3 w-3 text-blue-400" />
                                  {group.jiraEvents.length} event{group.jiraEvents.length !== 1 ? 's' : ''}
                                </span>
                                {(totalAdditions > 0 || totalDeletions > 0) && (
                                  <span>
                                    <span className="text-green-400">+{totalAdditions}</span>
                                    {' / '}
                                    <span className="text-red-400">-{totalDeletions}</span>
                                  </span>
                                )}
                                <span>{uniqueAuthors.join(', ')}</span>
                              </div>
                            </div>
                          </button>

                          {isExpanded && (
                            <div className={`px-4 pb-3 space-y-2 ${isDark ? 'bg-black/10' : 'bg-white/50'}`}>
                              {/* Commits sub-section */}
                              <div>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setLinkedCommitsCollapsed(prev => { const next = new Set(prev); if (next.has(group.issueKey)) next.delete(group.issueKey); else next.add(group.issueKey); return next; }); }}
                                  className={`flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider px-1 py-1.5 hover:opacity-80 transition-opacity ${isDark ? 'text-orange-400/70' : 'text-orange-600'}`}>
                                  {linkedCommitsCollapsed.has(group.issueKey) ? <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" /> : <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3" />}
                                  Commits ({group.commits.length})
                                </button>
                                {!linkedCommitsCollapsed.has(group.issueKey) && (() => {
                                  const sorted = [...group.commits].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
                                  const limit = linkedCommitLimit.get(group.issueKey) || 10;
                                  const visible = sorted.slice(0, limit);
                                  const remaining = sorted.length - limit;
                                  return (
                                    <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-slate-700/30' : 'border-slate-200'}`}>
                                      {visible.map(commit => {
                                        const color = repoColorMap.get(commit.repoId) || PROJECT_COLORS[0];
                                        const isCommitSelected = selectedItemId === `git-${commit.hash}`;
                                        return (
                                          <div key={commit.hash}>
                                            <div
                                              onClick={() => handleItemClick({ id: `git-${commit.hash}`, timestamp: commit.date, source: 'git', userName: commit.author, userEmail: commit.email, gitCommit: commit })}
                                              className={`flex items-start gap-2 px-3 py-2 border-b last:border-b-0 cursor-pointer transition-colors ${isDark ? 'border-slate-700/20 hover:bg-slate-700/20' : 'border-slate-100 hover:bg-slate-50'} ${isCommitSelected ? (isDark ? 'bg-orange-500/5' : 'bg-orange-50') : ''}`}>
                                              <FontAwesomeIcon icon={faCodeCommit} className="h-3.5 w-3.5 mt-0.5 text-orange-400 flex-shrink-0" />
                                              <div className="flex-1 min-w-0">
                                                <div className="flex items-center gap-1.5">
                                                  <span className={`text-[9px] px-1 py-0.5 rounded ${color.light} ${color.text} border ${color.border}`}>{commit.repoName}</span>
                                                  <span className={`text-xs truncate ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{commit.message.split('\n')[0]}</span>
                                                </div>
                                                <div className="flex items-center gap-2 mt-0.5 text-[10px] text-slate-500">
                                                  <span className="font-mono">{commit.hash.slice(0, 7)}</span>
                                                  <span>{commit.branch}</span>
                                                  {(commit.additions > 0 || commit.deletions > 0) && (
                                                    <span><span className="text-green-400">+{commit.additions}</span> / <span className="text-red-400">-{commit.deletions}</span></span>
                                                  )}
                                                </div>
                                              </div>
                                              <div className="text-right flex-shrink-0">
                                                <div className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{commit.author}</div>
                                                <div className="text-[10px] text-slate-500">{formatTime(commit.date)}</div>
                                              </div>
                                            </div>
                                            {isCommitSelected && (
                                              <div className={`border-b ${isDark ? 'border-slate-700/20 bg-slate-950/50' : 'border-slate-100 bg-slate-50'}`}>
                                                {diffLoading ? (
                                                  <div className="flex items-center justify-center gap-2 py-4">
                                                    <FontAwesomeIcon icon={faSpinner} className="h-4 w-4 animate-spin text-slate-400" />
                                                    <span className="text-xs text-slate-400">Loading diff...</span>
                                                  </div>
                                                ) : parsedDiffFiles.length > 0 ? (
                                                  <div className="p-2 space-y-1">
                                                    <div className={`flex items-center justify-between px-2 py-1.5 rounded-lg ${isDark ? 'bg-white/[0.03]' : 'bg-gray-100'}`}>
                                                      <span className="text-[10px] font-medium text-slate-400">
                                                        {parsedDiffFiles.length} file{parsedDiffFiles.length !== 1 ? 's' : ''}
                                                        <span className="ml-1.5 text-emerald-400">+{parsedDiffFiles.reduce((s, f) => s + f.additions, 0)}</span>
                                                        <span className="ml-1 text-rose-400">-{parsedDiffFiles.reduce((s, f) => s + f.deletions, 0)}</span>
                                                      </span>
                                                      <div className="flex items-center gap-1">
                                                        <button onClick={(e) => { e.stopPropagation(); expandAllDiffFiles(); }} className="text-[10px] px-1.5 py-0.5 rounded hover:bg-white/10 text-white/50">Expand all</button>
                                                        <button onClick={(e) => { e.stopPropagation(); collapseAllDiffFiles(); }} className="text-[10px] px-1.5 py-0.5 rounded hover:bg-white/10 text-white/50">Collapse all</button>
                                                      </div>
                                                    </div>
                                                    {parsedDiffFiles.map((file, fileIdx) => {
                                                      const isFileExpanded = expandedDiffFiles.has(fileIdx);
                                                      const totalChanges = file.additions + file.deletions;
                                                      const addPct = totalChanges > 0 ? Math.round((file.additions / totalChanges) * 100) : 0;
                                                      return (
                                                        <div key={fileIdx} className={`rounded-lg border overflow-hidden ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
                                                          <button onClick={(e) => { e.stopPropagation(); toggleDiffFile(fileIdx); }}
                                                            className={`w-full flex items-center gap-2 px-2 py-1.5 text-left transition-colors ${isDark ? 'bg-white/[0.03] hover:bg-white/[0.06]' : 'bg-gray-50 hover:bg-gray-100'}`}>
                                                            {isFileExpanded ? <FontAwesomeIcon icon={faChevronDown} className="h-3 w-3 text-slate-500" /> : <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3 text-slate-500" />}
                                                            <FontAwesomeIcon icon={faFile} className={`h-3 w-3 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                                                            <span className={`text-[10px] font-mono truncate flex-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>{file.filename}</span>
                                                            <div className="flex items-center gap-1 flex-shrink-0 ml-1">
                                                              <span className="text-[10px] font-mono text-emerald-400">+{file.additions}</span>
                                                              <span className="text-[10px] font-mono text-rose-400">-{file.deletions}</span>
                                                              <div className="flex h-1.5 w-8 rounded-full overflow-hidden bg-gray-700/20">
                                                                <div className="bg-emerald-500" style={{ width: `${addPct}%` }} />
                                                                <div className="bg-rose-500" style={{ width: `${100 - addPct}%` }} />
                                                              </div>
                                                            </div>
                                                          </button>
                                                          {isFileExpanded && (
                                                            <div className="overflow-x-auto">
                                                              {file.lines.map((line, lineIdx) => {
                                                                let lineClass = `font-mono text-[10px] whitespace-pre px-3 py-0.5 ${isDark ? 'text-white/80' : 'text-gray-800'}`;
                                                                if (line.startsWith('+') && !line.startsWith('+++')) lineClass = `font-mono text-[10px] whitespace-pre px-3 py-0.5 ${isDark ? 'bg-green-500/10 text-green-300' : 'bg-green-100 text-green-800'}`;
                                                                else if (line.startsWith('-') && !line.startsWith('---')) lineClass = `font-mono text-[10px] whitespace-pre px-3 py-0.5 ${isDark ? 'bg-red-500/10 text-red-300' : 'bg-red-100 text-red-800'}`;
                                                                else if (line.startsWith('@@')) lineClass = `font-mono text-[10px] whitespace-pre px-3 py-0.5 ${isDark ? 'bg-blue-500/10 text-blue-300' : 'bg-blue-100 text-blue-800'}`;
                                                                else if (line.startsWith('diff --git') || line.startsWith('index ') || line.startsWith('---') || line.startsWith('+++')) lineClass = `font-mono text-[10px] whitespace-pre px-3 py-0.5 font-semibold ${isDark ? 'text-white/50 bg-white/[0.02]' : 'text-gray-500 bg-gray-50'}`;
                                                                return <div key={lineIdx} className={lineClass}>{line || ' '}</div>;
                                                              })}
                                                            </div>
                                                          )}
                                                        </div>
                                                      );
                                                    })}
                                                  </div>
                                                ) : diff ? (
                                                  <pre className="p-3 font-mono text-[10px] whitespace-pre-wrap text-slate-400">{diff}</pre>
                                                ) : null}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                      {remaining > 0 && (
                                        <div className={`border-t px-3 py-2 text-center ${isDark ? 'border-slate-700/20' : 'border-slate-100'}`}>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); setLinkedCommitLimit(prev => { const next = new Map(prev); next.set(group.issueKey, limit + 10); return next; }); }}
                                            className="text-[10px] text-blue-400 hover:text-blue-300 transition-colors"
                                          >
                                            Show {Math.min(remaining, 10)} more commit{Math.min(remaining, 10) !== 1 ? 's' : ''} ({remaining} remaining)
                                          </button>
                                        </div>
                                      )}
                                      {limit > 10 && sorted.length > 10 && (
                                        <div className={`border-t px-3 py-1.5 text-center ${isDark ? 'border-slate-700/20' : 'border-slate-100'}`}>
                                          <button
                                            onClick={(e) => { e.stopPropagation(); setLinkedCommitLimit(prev => { const next = new Map(prev); next.set(group.issueKey, 10); return next; }); }}
                                            className="text-[10px] text-slate-500 hover:text-slate-400 transition-colors"
                                          >
                                            Show fewer
                                          </button>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })()}

                              </div>

                              {/* Jira events sub-section */}
                              <div>
                                <h4 className={`text-[10px] font-medium uppercase tracking-wider px-1 py-1.5 ${isDark ? 'text-blue-400/70' : 'text-blue-600'}`}>
                                  Jira Activity ({group.jiraEvents.length})
                                </h4>
                                <div className={`rounded-lg border overflow-hidden ${isDark ? 'border-slate-700/30' : 'border-slate-200'}`}>
                                  {group.jiraEvents
                                    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                                    .map(event => {
                                      const eIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
                                        status_change: mkIcon(faArrowRightArrowLeft), assignment: mkIcon(faUserPen), sprint_change: mkIcon(faCodeBranch),
                                        priority_change: mkIcon(faFlag), created: mkIcon(faPlus), resolved: mkIcon(faCircleCheck),
                                      };
                                      const eColorMap: Record<string, string> = {
                                        status_change: 'text-blue-400', assignment: 'text-purple-400', sprint_change: 'text-cyan-400',
                                        priority_change: 'text-orange-400', created: 'text-emerald-400', resolved: 'text-green-400',
                                      };
                                      const EIcon = eIconMap[event.type] || mkIcon(faBolt);
                                      const eColor = eColorMap[event.type] || 'text-slate-400';

                                      return (
                                        <div key={event.id}
                                          className={`flex items-start gap-2 px-3 py-2 border-b last:border-b-0 ${isDark ? 'border-slate-700/20' : 'border-slate-100'}`}>
                                          <EIcon className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${eColor}`} />
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                              <span className={`text-[10px] capitalize ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                                                {event.type.replace(/_/g, ' ')}
                                              </span>
                                              {event.type === 'status_change' && event.fromStatus && event.toStatus && (
                                                <>
                                                  <span className={`px-1.5 py-0.5 text-[9px] rounded border ${getStatusColor(event.fromStatus)}`}>{event.fromStatus}</span>
                                                  <span className="text-slate-500 text-[10px]">→</span>
                                                  <span className={`px-1.5 py-0.5 text-[9px] rounded border ${getStatusColor(event.toStatus)}`}>{event.toStatus}</span>
                                                </>
                                              )}
                                              {event.type === 'assignment' && (
                                                <span className="text-[10px] text-slate-400">
                                                  {event.fromAssignee || 'Unassigned'} → <span className="text-purple-300">{event.toAssignee || 'Unassigned'}</span>
                                                </span>
                                              )}
                                              {event.type === 'sprint_change' && (
                                                <span className="text-[10px] text-slate-400">
                                                  {event.fromSprint && <>{event.fromSprint} → </>}
                                                  <span className="text-cyan-300">{event.toSprint || 'Removed'}</span>
                                                </span>
                                              )}
                                              {event.type === 'created' && <span className="text-[10px] text-emerald-400">Issue created</span>}
                                              {event.type === 'resolved' && <span className="text-[10px] text-green-400">Resolved: {event.toValue}</span>}
                                            </div>
                                          </div>
                                          <div className="text-right flex-shrink-0">
                                            <div className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{event.author.displayName}</div>
                                            <div className="text-[9px] text-slate-500">{new Date(event.timestamp).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} {formatTime(event.timestamp)}</div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                </div>
                              </div>

                              {/* Jira issue detail inline */}
                              {hasJira && (
                                <div className="mt-1">
                                  <JiraIssueDetail
                                    issueKey={group.issueKey}
                                    jiraDomain={jiraDomain!}
                                    jiraEmail={jiraEmail!}
                                    jiraApiToken={jiraApiToken!}
                                    onClose={() => setExpandedLinkedKeys(prev => { const next = new Set(prev); next.delete(group.issueKey); return next; })}
                                    onNavigate={(key) => {
                                      setExpandedLinkedKeys(prev => { const next = new Set(prev); next.add(key); return next; });
                                    }}
                                    relatedCommits={group.commits}
                                    availableRepos={availableRepos}
                                    storedUsername={storedUsername}
                                    storedToken={storedToken}
                                  />
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  )}
                </div>
              )}

              {timeline.length === 0 && (
                <div className="text-center py-16">
                  <FontAwesomeIcon icon={faMagnifyingGlass} className={`h-8 w-8 mx-auto mb-3 ${isDark ? 'text-slate-600' : 'text-slate-400'}`} />
                  <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>No events match your filters</p>
                </div>
              )}

              {/* ===== Grouped by Ticket view ===== */}
              {groupByTicket && combinedTicketGroups.length > 0 && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between px-1 mb-2">
                    <span className={`text-[10px] uppercase tracking-wider font-medium ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                      {combinedTicketGroups.length} ticket{combinedTicketGroups.length !== 1 ? 's' : ''}
                      {ungroupedGitCommits.length > 0 && <> &middot; {ungroupedGitCommits.length} unlinked commit{ungroupedGitCommits.length !== 1 ? 's' : ''}</>}
                    </span>
                  </div>
                  {combinedTicketGroups.map((group) => {
                    const isTicketExpanded = expandedTickets.has(group.issueKey);
                    const eIconMap: Record<string, React.ComponentType<{ className?: string }>> = {
                      status_change: mkIcon(faArrowRightArrowLeft), assignment: mkIcon(faUserPen), sprint_change: mkIcon(faCodeBranch),
                      priority_change: mkIcon(faFlag), created: mkIcon(faPlus), resolved: mkIcon(faCircleCheck),
                    };
                    const eColorMap: Record<string, string> = {
                      status_change: 'text-blue-400', assignment: 'text-purple-400', sprint_change: 'text-cyan-400',
                      priority_change: 'text-orange-400', created: 'text-emerald-400', resolved: 'text-green-400',
                    };
                    const eventTypes = [...new Set(group.jiraEvents.map(e => e.type))];
                    const authors = [...new Set(group.jiraEvents.map(e => e.author.displayName))];

                    return (
                      <div key={group.issueKey} id={`combined-ticket-${group.issueKey}`} className={`${isDark ? 'bg-slate-800/30 border-slate-700/30' : 'bg-white border-slate-200'} border rounded-xl overflow-hidden`}>
                        <button onClick={() => toggleTicket(group.issueKey)}
                          className={`w-full px-4 py-3 flex items-start gap-3 transition-colors text-left ${isDark ? 'hover:bg-slate-700/10' : 'hover:bg-slate-50'}`}>
                          <div className="mt-0.5 flex-shrink-0 flex items-center gap-1 text-blue-400">
                            {isTicketExpanded ? <FontAwesomeIcon icon={faChevronDown} className="h-3.5 w-3.5 text-slate-500" /> : <FontAwesomeIcon icon={faChevronRight} className="h-3.5 w-3.5 text-slate-500" />}
                            <FontAwesomeIcon icon={faLayerGroup} className="h-4 w-4 text-indigo-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              {jiraDomain && (
                                <a href={`https://${jiraDomain}.atlassian.net/browse/${group.issueKey}`}
                                  target="_blank" rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-xs font-mono text-blue-400 hover:text-blue-300 font-medium">
                                  {group.issueKey}
                                </a>
                              )}
                              {!jiraDomain && <span className="text-xs font-mono font-medium text-blue-400">{group.issueKey}</span>}
                              <span className={`text-xs truncate max-w-[280px] ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{group.issueSummary}</span>
                              <span className={`text-[10px] px-1.5 py-0.5 rounded border ${
                                group.issueType === 'Epic' ? 'bg-purple-500/15 text-purple-300 border-purple-500/20' :
                                group.issueType === 'Bug' ? 'bg-red-500/15 text-red-300 border-red-500/20' :
                                group.issueType === 'Sub-task' ? 'bg-cyan-500/15 text-cyan-300 border-cyan-500/20' :
                                group.issueType === 'Story' ? 'bg-green-500/15 text-green-300 border-green-500/20' :
                                'bg-slate-700/50 text-slate-400 border-slate-600/30'
                              }`}>{group.issueType}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                              {group.latestStatus && (
                                <span className={`px-1.5 py-0.5 text-[10px] rounded border ${getStatusColor(group.latestStatus)}`}>{group.latestStatus}</span>
                              )}
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? 'text-slate-500 bg-slate-700/50' : 'text-slate-400 bg-slate-100'}`}>
                                {group.jiraEvents.length} event{group.jiraEvents.length !== 1 ? 's' : ''}
                              </span>
                              {group.gitCommits.length > 0 && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-400 border border-orange-500/20">
                                  {group.gitCommits.length} commit{group.gitCommits.length !== 1 ? 's' : ''}
                                </span>
                              )}
                              {eventTypes.map(t => {
                                const EIcon = eIconMap[t] || mkIcon(faBolt);
                                const eColor = eColorMap[t] || 'text-slate-400';
                                return <span key={t} title={t.replace(/_/g, ' ')}><EIcon className={`h-3 w-3 ${eColor}`} /></span>;
                              })}
                            </div>
                          </div>
                          <div className="flex-shrink-0 text-right">
                            <div className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{authors.length > 1 ? `${authors.length} people` : authors[0]}</div>
                            <div className="text-[10px] text-slate-500">{formatTime(group.latestTimestamp)}</div>
                          </div>
                        </button>
                        {isTicketExpanded && (
                          <div className={`border-t ${isDark ? 'border-slate-700/20' : 'border-slate-100'}`}>
                            {/* Jira events */}
                            {group.jiraEvents
                              .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
                              .map(event => {
                                const EIcon = eIconMap[event.type] || mkIcon(faBolt);
                                const eColor = eColorMap[event.type] || 'text-slate-400';
                                return (
                                  <div key={event.id} className={`flex items-start gap-2 px-4 py-2 border-b last:border-b-0 ${isDark ? 'border-slate-700/10' : 'border-slate-100'}`}>
                                    <EIcon className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${eColor}`} />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className={`text-[10px] capitalize ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>{event.type.replace(/_/g, ' ')}</span>
                                        {event.type === 'status_change' && event.fromStatus && event.toStatus && (
                                          <>
                                            <span className={`px-1.5 py-0.5 text-[9px] rounded border ${getStatusColor(event.fromStatus)}`}>{event.fromStatus}</span>
                                            <span className="text-slate-500 text-[10px]">→</span>
                                            <span className={`px-1.5 py-0.5 text-[9px] rounded border ${getStatusColor(event.toStatus)}`}>{event.toStatus}</span>
                                          </>
                                        )}
                                        {event.type === 'assignment' && (
                                          <span className="text-[10px] text-slate-400">{event.fromAssignee || 'Unassigned'} → <span className="text-purple-300">{event.toAssignee || 'Unassigned'}</span></span>
                                        )}
                                        {event.type === 'sprint_change' && (
                                          <span className="text-[10px] text-slate-400">{event.fromSprint && <>{event.fromSprint} → </>}<span className="text-cyan-300">{event.toSprint || 'Removed'}</span></span>
                                        )}
                                        {event.type === 'created' && <span className="text-[10px] text-emerald-400">Issue created</span>}
                                        {event.type === 'resolved' && <span className="text-[10px] text-green-400">Resolved: {event.toValue}</span>}
                                      </div>
                                    </div>
                                    <div className="text-right flex-shrink-0">
                                      <div className={`text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>{event.author.displayName}</div>
                                      <div className="text-[9px] text-slate-500">{new Date(event.timestamp).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} {formatTime(event.timestamp)}</div>
                                    </div>
                                  </div>
                                );
                              })}
                            {/* Linked git commits */}
                            {group.gitCommits.length > 0 && (
                              <div className={`border-t px-4 py-2 ${isDark ? 'border-slate-700/20' : 'border-slate-100'}`}>
                                <h4 className={`text-[10px] font-medium uppercase tracking-wider mb-1.5 ${isDark ? 'text-orange-400/70' : 'text-orange-600'}`}>
                                  Linked Commits ({group.gitCommits.length})
                                </h4>
                                {group.gitCommits.slice(0, 5).map(commit => (
                                  <div key={commit.hash} className={`flex items-center gap-2 py-1 text-[10px] ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
                                    <FontAwesomeIcon icon={faCodeCommit} className="h-3 w-3 text-orange-400 flex-shrink-0" />
                                    <span className="font-mono text-slate-500">{commit.hash.slice(0, 7)}</span>
                                    <span className="truncate flex-1">{commit.message.split('\n')[0]}</span>
                                    <span className="flex-shrink-0">{commit.author}</span>
                                  </div>
                                ))}
                                {group.gitCommits.length > 5 && (
                                  <div className="text-[10px] text-slate-500 mt-1">+{group.gitCommits.length - 5} more</div>
                                )}
                              </div>
                            )}
                            {/* Inline Jira detail */}
                            {hasJira && (
                              <div className={`border-t ${isDark ? 'border-slate-700/20' : 'border-slate-100'}`}>
                                <JiraIssueDetail
                                  issueKey={group.issueKey}
                                  jiraDomain={jiraDomain!}
                                  jiraEmail={jiraEmail!}
                                  jiraApiToken={jiraApiToken!}
                                  onClose={() => toggleTicket(group.issueKey)}
                                  onNavigate={(key) => { setExpandedTickets(prev => { const next = new Set(prev); next.add(key); return next; }); }}
                                  relatedCommits={group.gitCommits}
                                  availableRepos={availableRepos}
                                  storedUsername={storedUsername}
                                  storedToken={storedToken}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Unlinked git commits */}
                  {ungroupedGitCommits.length > 0 && (
                    <div className={`${isDark ? 'bg-slate-800/30 border-slate-700/30' : 'bg-white border-slate-200'} border rounded-xl overflow-hidden mt-3`}>
                      <div className={`px-4 py-2.5 flex items-center gap-2 ${isDark ? 'bg-orange-500/5' : 'bg-orange-50'}`}>
                        <FontAwesomeIcon icon={faCodeCommit} className="h-3.5 w-3.5 text-orange-400" />
                        <span className={`text-xs font-medium ${isDark ? 'text-orange-300' : 'text-orange-700'}`}>
                          Unlinked Git Commits ({ungroupedGitCommits.length})
                        </span>
                      </div>
                      <div className={`border-t ${isDark ? 'border-slate-700/20' : 'border-slate-100'}`}>
                        {ungroupedGitCommits.slice(0, 20).map(renderTimelineItem)}
                        {ungroupedGitCommits.length > 20 && (
                          <div className={`text-center py-2 text-[10px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                            +{ungroupedGitCommits.length - 20} more commits
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ===== Normal date-grouped view ===== */}
              {!groupByTicket && groupedByDate.map(([date, items]) => {
                const isExpanded = expandedDates.has(date);
                const gitCount = items.filter(i => i.source === 'git').length;
                const jiraCount = items.filter(i => i.source === 'jira').length;
                const confluenceCount = items.filter(i => i.source === 'confluence').length;

                return (
                  <div key={date} className={`${isDark ? 'bg-slate-800/30 border-slate-700/30' : 'bg-white border-slate-200'} border rounded-xl overflow-hidden`}>
                    <button onClick={() => toggleDate(date)}
                      className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${isDark ? 'hover:bg-slate-700/20' : 'hover:bg-slate-50'}`}>
                      <div className="flex items-center gap-2">
                        {isExpanded ? <FontAwesomeIcon icon={faChevronDown} className="h-4 w-4 text-slate-500" /> : <FontAwesomeIcon icon={faChevronRight} className="h-4 w-4 text-slate-500" />}
                        <span className={`text-sm font-medium ${date === toLocalDateStr() ? 'text-blue-400' : (isDark ? 'text-white' : 'text-slate-900')}`}>
                          {formatDateLabel(date)}
                        </span>
                        <div className="flex items-center gap-1.5">
                          {gitCount > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-400 border border-orange-500/20">
                              {gitCount} commit{gitCount !== 1 ? 's' : ''}
                            </span>
                          )}
                          {jiraCount > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/20">
                              {jiraCount} jira
                            </span>
                          )}
                          {confluenceCount > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/10 text-sky-400 border border-sky-500/20">
                              {confluenceCount} pages
                            </span>
                          )}
                        </div>
                      </div>
                      <span className={`text-[10px] ${isDark ? 'text-slate-600' : 'text-slate-400'}`}>{date}</span>
                    </button>
                    {isExpanded && (
                      <div className={`border-t ${isDark ? 'border-slate-700/20' : 'border-slate-100'}`}>
                        {items.map(renderTimelineItem)}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
        </div>
        )}

        {/* Loading state */}
        {loading && (
          <div className="flex items-center justify-center py-12 px-6">
            <div className={`w-full max-w-md rounded-2xl border p-8 ${isDark ? 'bg-slate-900/60 border-white/10' : 'bg-white border-slate-200 shadow-lg'}`}>
              {/* Animated icon */}
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className={`h-16 w-16 rounded-full flex items-center justify-center ${isDark ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
                    <FontAwesomeIcon icon={faSpinner} className="h-8 w-8 text-blue-500 animate-spin" />
                  </div>
                  <div className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
                    <span className="text-[9px] font-bold text-white">{gitProgressIndex}</span>
                  </div>
                </div>
              </div>

              {/* Title */}
              <h3 className={`text-center text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {gitLoading && jiraLoading ? 'Fetching Git + Jira data' : gitLoading ? 'Analyzing Repositories' : 'Fetching Jira Activity'}
              </h3>
              <p className={`text-center text-xs mb-6 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>This may take a moment for large projects</p>

              {/* Git progress */}
              {gitLoading && gitProgressTotal > 0 && (
                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Git Repositories</span>
                    <span className={`text-xs font-mono font-bold ${isDark ? 'text-cyan-400' : 'text-cyan-600'}`}>{gitProgressIndex} / {gitProgressTotal}</span>
                  </div>
                  <div className={`w-full h-2.5 rounded-full overflow-hidden ${isDark ? 'bg-white/5' : 'bg-slate-100'}`}>
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-blue-500 via-blue-400 to-cyan-400 transition-all duration-700 ease-out"
                      style={{ width: `${Math.max(2, (gitProgressIndex / gitProgressTotal) * 100)}%` }}
                    />
                  </div>
                  <div className={`flex items-center gap-2 px-3 py-2 rounded-lg ${isDark ? 'bg-white/[0.03]' : 'bg-slate-50'}`}>
                    <div className="h-1.5 w-1.5 rounded-full bg-blue-500 animate-pulse flex-shrink-0" />
                    <p className={`text-[11px] truncate ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>{gitProgressMessage || 'Preparing...'}</p>
                  </div>
                </div>
              )}

              {/* Jira progress */}
              {jiraLoading && (
                <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg ${isDark ? 'bg-white/[0.03]' : 'bg-slate-50'}`}>
                  <FontAwesomeIcon icon={faSpinner} className="h-3.5 w-3.5 animate-spin text-indigo-400 flex-shrink-0" />
                  <span className={`text-[11px] font-medium ${isDark ? 'text-indigo-300' : 'text-indigo-600'}`}>Loading Jira activity...</span>
                </div>
              )}

              {/* Overall percentage */}
              {gitProgressTotal > 0 && (
                <div className="mt-5 text-center">
                  <span className={`text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent`}>
                    {Math.round((gitProgressIndex / gitProgressTotal) * 100)}%
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && dataReady && totalGit === 0 && totalJira === 0 && !error && !showSelector && (
          <div className="text-center py-16">
            <FontAwesomeIcon icon={faLayerGroup} className={`h-10 w-10 mx-auto mb-4 ${isDark ? 'text-slate-600' : 'text-slate-400'}`} />
            <h3 className={`text-lg font-medium mb-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>No data yet</h3>
            <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Select repositories and a Jira project, then click Analyze.</p>
            <button onClick={() => setShowSelector(true)} className="mt-4 text-sm text-blue-400 hover:text-blue-300">Show Selector</button>
          </div>
        )}
        
        {/* Token Expiry Modal */}
        <TokenExpiryModal />
        </div>
      </div>

    </div>
  );
}

export default CombinedProjectView;
